import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-datepicker/dist/react-datepicker.css";
import DatePicker from "react-datepicker";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { motion, AnimatePresence } from "framer-motion";
import moment from "moment-timezone";
import SEO from "../components/SEO/SEO";
import VehicleServiceInfo from "../components/VehicleManagement/VehicleServiceInfo";
import {
  FaArrowLeft,
  FaInfoCircle,
  FaShieldAlt,
  FaUser,
  FaCarSide,
  FaStar,
  FaCheck,
  FaMapMarkerAlt,
  FaCalendarAlt,
  FaClock,
  FaGasPump,
  FaCog,
  FaUsers,
  FaDoorOpen,
  FaSuitcase,
  FaSnowflake,
  FaReceipt,
  FaBuilding,
  FaIdCard,
  FaMapPin,
  FaCreditCard
} from "react-icons/fa";
import { GiCarDoor, GiGps } from "react-icons/gi";
import { PiGasCanFill, PiSuitcaseRollingBold } from "react-icons/pi";
import { HiSparkles, HiLightningBolt } from "react-icons/hi";
import Swal from "sweetalert2";
import Header from "../components/Header/Header";
import Footer from "../components/Footer/Footer";
import "react-phone-number-input/style.css";
import PhoneInput from "react-phone-number-input";
import { slugifyCar } from "../utils/slugify";
import ImportantInfoModal from "../components/ImportantInfoModal/ImportantInfoModal";
import InsuranceModal from "../components/InsuranceModal/InsuranceModal";
import SuccessModal from "../components/SuccessModal/SuccessModal";
// import PaymentModal from "../components/PaymentModal/PaymentModal"; // Commented out for now
import LoadingSpinner from "../components/LoadingSpinner/LoadingSpinner";
import { rentals, coupons, cars, auth, locations, additionalOptions, oxygen } from "../services/api";
import logo from "../assets/images/logo-1.png";

// Import all car images (keeping your existing imports)
import OpelCorsa from "../assets/images/opelcorsa.jpg";
import SpaceStar from "../assets/images/MITS-SPACE.png";
import Peugeot208 from "../assets/images/p-208.jpg";
import Peugeot108 from "../assets/images/108-p.webp";
import CitroenC1 from "../assets/images/C1.jpeg";
import Micra from "../assets/images/PHOTO-2022-01-23-14-12-06.jpg";
import Panda from "../assets/images/PHOTO-2022-01-23-14-12-05-1.jpg";
import Stonic from "../assets/images/KIA-STONIC-Manual-1.png";
import i20 from "../assets/images/HYUNDAI-i20-Manual-1.png";
import quashqai from "../assets/images/quashqai.jpg";
import duster from "../assets/images/duster.jpeg";
import crossland from "../assets/images/opel-crossland.webp";
import clio from "../assets/images/RENAULT-CLIO-Manual-1.png";
import rio from "../assets/images/KIA-RIO-Auto-1.png";
import Wrangler5D from "../assets/images/jeep-wr-5d.png";
import WranglerCabrio from "../assets/images/Jeep-wrangler-cabrio.png";
import Discovery from "../assets/images/land-rover.webp";
import Wrangler from "../assets/images/JEEP-WRANGLER-Auto-1.png";
import WranglerSahara from "../assets/images/JEEP-WRANGLER-1.png.webp";
import Berlingo from "../assets/images/CITROEN-BERLINGO-Auto.png.webp";
import Doblo from "../assets/images/FIAT-DOBLO-Manual-1.png.webp";
import TRoc from "../assets/images/vw-t-roc.png";
import C1Cabrio from "../assets/images/PHOTO-2022-01-23-14-12-11.jpg.webp";
import MiniCooper from "../assets/images/MINI-COOPER-Cabrio-Auto-1.png.webp";
import Fiat500 from "../assets/images/FIAT-500-Auto-Cabrio-1.png.webp";
import SmartForTwo from "../assets/images/SMART-ForTwo-Cabrio-Manual-4.png.webp";
import SmartForTwoAuto from "../assets/images/SMART-ForTwo-Cabrio-Auto-2.png.webp";
import Buggy800 from "../assets/images/CF-MOTO-BUGGY-800cc-1.png.webp";
import Buggy550 from "../assets/images/CF-MOTO-BUGGY-550cc-1.png.webp";
import ATV450 from "../assets/images/5_14f72470629695dca3487f902b2c012f.png.webp";
import RangeSport from "../assets/images/PHOTO-2022-01-22-12-09-42.jpg";
import C3Aircross from "../assets/images/PHOTO-2022-01-25-15-13-22.jpg";
import C3 from "../assets/images/PHOTO-2022-01-22-11-45-44-1.jpg";
import C4Cactus from "../assets/images/PHOTO-2022-01-22-11-51-47.jpg";
import MiniCountryman from "../assets/images/PHOTO-2022-01-25-15-13-22-1.jpg";
import Glb2022 from "../assets/images/MERCEDES-BENZ-GLB-2022-Automatic-1.png";
import Gla2023 from "../assets/images/253_9060274dbdc76eb56b4a456a70298fff.png";
import BmwX1 from "../assets/images/BMW-X1-2023-Automatic.png";
import BmwX2 from "../assets/images/BMW-X2-Automatic-1.png";
import Sportage from "../assets/images/KIA-SPORTAGE-Auto-1.png";
import Cayenne from "../assets/images/PORSCHE-CAYENNE-Auto-1.png";
import Bike125 from "../assets/images/125bike.png";

const imageMap = {
  "opelcorsa.jpg": OpelCorsa,
  "MITS-SPACE.png": SpaceStar,
  "p-208.jpg": Peugeot208,
  "108-p.webp": Peugeot108,
  "C1.jpeg": CitroenC1,
  "PHOTO-2022-01-23-14-12-06.jpg": Micra,
  "PHOTO-2022-01-23-14-12-05-1.jpg": Panda,
  "KIA-STONIC-Manual-1.png": Stonic,
  "HYUNDAI-i20-Manual-1.png": i20,
  "quashqai.jpg": quashqai,
  "duster.jpeg": duster,
  "opel-crossland.webp": crossland,
  "RENAULT-CLIO-Manual-1.png": clio,
  "KIA-RIO-Auto-1.png": rio,
  "jeep-wr-5d.png": Wrangler5D,
  "Jeep-wrangler-cabrio.png": WranglerCabrio,
  "land-rover.webp": Discovery,
  "JEEP-WRANGLER-Auto-1.png": Wrangler,
  "JEEP-WRANGLER-1.png.webp": WranglerSahara,
  "CITROEN-BERLINGO-Auto.png.webp": Berlingo,
  "FIAT-DOBLO-Manual-1.png.webp": Doblo,
  "vw-t-roc.png": TRoc,
  "PHOTO-2022-01-23-14-12-11.jpg.webp": C1Cabrio,
  "MINI-COOPER-Cabrio-Auto-1.png.webp": MiniCooper,
  "FIAT-500-Auto-Cabrio-1.png.webp": Fiat500,
  "SMART-ForTwo-Cabrio-Manual-4.png.webp": SmartForTwo,
  "SMART-ForTwo-Cabrio-Auto-2.png.webp": SmartForTwoAuto,
  "CF-MOTO-BUGGY-800cc-1.png.webp": Buggy800,
  "beach_buggy.png": Buggy550,
  "5_14f72470629695dca3487f902b2c012f.png.webp": ATV450,
  "PHOTO-2022-01-22-12-09-42.jpg": RangeSport,
  "PHOTO-2022-01-25-15-13-22.jpg": C3Aircross,
  "PHOTO-2022-01-22-11-45-44-1.jpg": C3,
  "PHOTO-2022-01-22-11-51-47.jpg": C4Cactus,
  "PHOTO-2022-01-25-15-13-22-1.jpg": MiniCountryman,
  "MERCEDES-BENZ-GLB-2022-Automatic-1.png": Glb2022,
  "253_9060274dbdc76eb56b4a456a70298fff.png": Gla2023,
  "BMW-X1-2023-Automatic.png": BmwX1,
  "BMW-X2-Automatic-1.png": BmwX2,
  "KIA-SPORTAGE-Auto-1.png": Sportage,
  "PORSCHE-CAYENNE-Auto-1.png": Cayenne,
  "125bike.png": Bike125,
};

// Enhanced Custom Components with modern styling
const CustomDateInput = React.forwardRef(
  ({ value, onClick, placeholder, isError }, ref) => (
    <motion.button
      type="button"
      className={`w-full h-10 sm:h-12 px-3 sm:px-4 rounded-lg sm:rounded-xl text-left bg-white border-2 transition-all duration-300 flex items-center justify-between group text-sm sm:text-base ${
        isError 
          ? "border-red-400 bg-red-50 text-red-600" 
          : "border-gray-200 hover:border-red-400 hover:shadow-md focus-within:border-red-500 focus-within:ring-2 focus-within:ring-red-100"
      }`}
      onClick={onClick}
      ref={ref}
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.99 }}
    >
      <span className={value ? "text-gray-800" : "text-gray-400"}>
        {value || placeholder}
      </span>
      <FaCalendarAlt className={`text-sm transition-colors ${isError ? 'text-red-400' : 'text-gray-400 group-hover:text-red-500'}`} />
    </motion.button>
  )
);

const CustomSelect = ({
  value,
  onChange,
  options,
  placeholder,
  onOtherLocationChange,
  showOtherInput,
  initialCustomValue = "",
  icon: Icon = FaMapMarkerAlt
}) => (
  <div className="space-y-3">
    <div className="relative">
      <select
        value={value}
        onChange={onChange}
        className="w-full h-10 sm:h-12 px-3 sm:px-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-white appearance-none hover:border-red-400 hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500 text-sm sm:text-base"
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon className="absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
      <div className="absolute inset-y-0 right-0 flex items-center px-4 pointer-events-none">
        <svg
          className="w-5 h-5 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            d="M19 9l-7 7-7-7"
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
          />
        </svg>
      </div>
    </div>
    <AnimatePresence>
      {showOtherInput && value === "other" && (
        <motion.input
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 40 }}
          exit={{ opacity: 0, height: 0 }}
          type="text"
          placeholder="Enter location details"
          defaultValue={initialCustomValue}
          onChange={(e) => onOtherLocationChange(e.target.value)}
          className="w-full h-10 sm:h-12 px-3 sm:px-4 pl-10 sm:pl-12 border-2 border-gray-200 rounded-lg sm:rounded-xl bg-white hover:border-red-400 hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500 text-sm sm:text-base"
        />
      )}
    </AnimatePresence>
  </div>
);

const CustomCheckbox = ({ checked, onChange, label, description }) => (
  <motion.label 
    className="flex items-start cursor-pointer group p-2 sm:p-3 rounded-lg sm:rounded-xl hover:bg-red-50 transition-all duration-300 border border-transparent hover:border-red-200"
    whileHover={{ scale: 1.01 }}
    whileTap={{ scale: 0.99 }}
  >
    <div className="relative w-5 h-5 sm:w-6 sm:h-6 mr-3 sm:mr-4 mt-0.5 flex-shrink-0">
      <input
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="peer hidden"
      />
      <div className="w-5 h-5 sm:w-6 sm:h-6 border-2 border-gray-300 rounded-lg transition-all duration-300 peer-checked:border-red-500 peer-checked:bg-red-500 group-hover:border-red-400 shadow-sm" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white opacity-0 peer-checked:opacity-100 transition-all duration-300 scale-0 peer-checked:scale-100">
        <FaCheck className="w-3 h-3" />
      </div>
    </div>
    <div className="flex-1">
      <span className="text-gray-800 group-hover:text-gray-900 transition-colors duration-200 font-medium">
        {label}
      </span>
      {description && (
        <p className="text-sm text-gray-500 mt-1">{description}</p>
      )}
    </div>
  </motion.label>
);

const CustomInput = ({
  type,
  placeholder,
  value,
  onChange,
  className = "",
  icon: Icon,
  error
}) => (
  <div className="relative">
    <input
      type={type}
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      className={`w-full h-10 sm:h-12 px-3 sm:px-4 ${Icon ? 'pl-10 sm:pl-12' : ''} border-2 rounded-lg sm:rounded-xl bg-white transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-100 text-sm sm:text-base ${
        error 
          ? "border-red-400 bg-red-50 text-red-600 focus:border-red-500" 
          : "border-gray-200 hover:border-red-400 hover:shadow-md focus:border-red-500"
      } ${className}`}
    />
    {Icon && (
      <Icon className={`absolute left-3 sm:left-4 top-1/2 transform -translate-y-1/2 text-sm ${
        error ? 'text-red-400' : 'text-gray-400'
      }`} />
    )}
  </div>
);

// Modern Car Specification Card Component
const SpecCard = ({ icon: Icon, label, value, highlight = false }) => (
  <motion.div 
    className={`p-3 sm:p-4 rounded-xl sm:rounded-2xl border-2 transition-all duration-300 ${
      highlight 
        ? "bg-gradient-to-br from-red-50 to-red-100 border-red-200" 
        : "bg-white border-gray-200 hover:border-red-200 hover:shadow-lg"
    }`}
    whileHover={{ scale: 1.02, y: -2 }}
    whileTap={{ scale: 0.98 }}
  >
    <div className="flex flex-col items-center text-center space-y-2">
      <div className={`p-2 sm:p-3 rounded-full ${
        highlight ? "bg-red-500 text-white" : "bg-gray-100 text-gray-600"
      }`}>
        <Icon className="text-lg sm:text-xl" />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
        <p className={`font-bold text-sm ${highlight ? "text-red-700" : "text-gray-800"}`}>
          {value || "-"}
        </p>
      </div>
    </div>
  </motion.div>
);

// Feature Badge Component
const FeatureBadge = ({ feature, isHighlight = false }) => (
  <motion.div
    className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
      isHighlight
        ? "bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg"
        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
    }`}
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
  >
    {feature}
  </motion.div>
);

const colors = {
  primary: "#D81919",
  secondary: "#990000",
  accent: "#FFEDED",
  dark: "#333333",
  light: "#FFFFFF",
  gray: "#F8F8F8",
};

// Keep all your existing utility functions (generateInvoice, etc.)
const generateInvoice = async (rental) => {
  const invoiceDiv = document.createElement("div");
  invoiceDiv.style.padding = "10px";
  invoiceDiv.style.position = "absolute";
  invoiceDiv.style.left = "-9999px";
  invoiceDiv.style.background = "white";
  invoiceDiv.style.width = "900px";

  const header = `
  <div style="display: flex; justify-content: space-between; margin-bottom: 40px; position: relative;">
    <div style="width: 66%; position: relative; overflow: hidden; height: 120px;">
      <div style="position: absolute; top: -20px; left: -200px; width: calc(100% + 220px); height: calc(100% + 40px); transform: skew(-15deg); z-index: 0; box-shadow: 0 3px 10px rgba(0,0,0,0.2);"></div>
      <img src="${logo}" alt="Logo" style="width: 85%; height: 100%; object-fit: contain; position: relative; z-index: 1; padding: 20px 20px 20px 40px;" />
    </div>
    <div style="width: 32%; text-align: right; padding: 20px; background: ${
      colors.gray
    }; border-right: 5px solid ${
    colors.primary
  }; box-shadow: 0 3px 8px rgba(0,0,0,0.1);">
      <h2 style="margin-bottom: 15px; color: ${
        colors.primary
      }; font-size: 22px; font-weight: bold;">Contact Information</h2>
      <p style="margin: 5px 0; color: ${
        colors.dark
      };">Epar. Odos Mykonos 846 00</p>
      <p style="margin: 5px 0; color: ${colors.dark};">nleont@gmail.com</p>
      <p style="margin: 5px 0; color: ${
        colors.dark
      }; font-weight: bold;">+306941419012</p>
      <p style="margin: 5px 0; color: ${
        colors.primary
      };">${window.location.origin}</p>
    </div>
  </div>
  <div style="text-align: center; margin-bottom: 30px; padding: 10px 0; border-bottom: 2px solid ${
    colors.primary
  }; border-top: 2px solid ${colors.primary};">
    <h1 style="margin: 0; color: ${
      colors.primary
    }; font-size: 28px; text-transform: uppercase; letter-spacing: 2px;">PRE-INVOICE</h1>
    <p style="margin: 5px 0; color: ${
      colors.dark
    };">Pre-Invoice #: INV-${new Date().getFullYear()}-${Math.floor(
    Math.random() * 1000000
  )}</p>
    <p style="margin: 5px 0; color: ${
      colors.dark
    };">Pre-Invoice Date: ${new Date().toLocaleDateString("en-GB")}</p>
  </div>
`;

  const infoSection = `
  <div style="display: flex; justify-content: space-between; margin-bottom: 40px; gap: 40px;">
    <div style="flex: 1; background: ${
      colors.accent
    }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
    colors.primary
  }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <h3 style="margin-bottom: 15px; color: ${
        colors.primary
      }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
    colors.primary
  }; padding-bottom: 8px;">CLIENT DETAILS</h3>
      <p style="margin: 10px 0; font-weight: bold; font-size: 16px;">${
        rental.customer.firstName
      } ${rental.customer.lastName}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Phone:</span> ${rental.customer.phoneNumber}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Email:</span> ${rental.customer.email}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Age:</span> ${rental.customer.age}</p>
    </div>
    <div style="flex: 1; background: ${
      colors.accent
    }; padding: 25px; border-radius: 8px; border-left: 6px solid ${
    colors.primary
  }; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <h3 style="margin-bottom: 15px; color: ${
        colors.primary
      }; text-transform: uppercase; font-weight: bold; border-bottom: 2px solid ${
    colors.primary
  }; padding-bottom: 8px;">VEHICLE INFORMATION</h3>
      <p style="margin: 10px 0; font-weight: bold; font-size: 16px;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Vehicle Name:</span> ${rental.carName}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Rental Period:</span> ${moment(rental.pickupDateTime).tz('Europe/Athens').format('DD/MM/YY HH:mm')} - ${moment(rental.returnDateTime).tz('Europe/Athens').format('DD/MM/YY HH:mm')}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Pick-up:</span> ${rental.pickupLocation}</p>
      <p style="margin: 8px 0;"><span style="color: ${
        colors.secondary
      }; font-weight: bold;">Return:</span> ${rental.returnLocation}</p>
    </div>
  </div>
`;

  const priceRows = rental.priceBreakdown
    .map(
      (period, index) => `
  <tr style="background-color: ${
    index % 2 === 0 ? colors.light : colors.accent
  };">
    <td style="padding: 14px; border-bottom: 1px solid #ddd; font-weight: bold;">
      Rental (${moment(period.startDate).tz('Europe/Athens').format('DD/MM/YY')} - ${moment(period.endDate).tz('Europe/Athens').format('DD/MM/YY')})
    </td>
    <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">€${
      period.pricePerDay
    }</td>
    <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">${
      period.numberOfDays
    }</td>
    <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">€${
      period.totalForPeriod
    }</td>
  </tr>
`
    )
    .join("");

  const additionalOptionsRows = [];
  if (rental.additionalOptions && Array.isArray(rental.additionalOptions) && rental.totalRentalDays !== undefined) {
    const totalDaysForInvoice = rental.totalRentalDays;
    rental.additionalOptions.forEach(option => {
      additionalOptionsRows.push(
        `<tr style="background-color: ${colors.accent};">
           <td style="padding: 14px; border-bottom: 1px solid #ddd;">${option.name} (Additional Option)</td>
           <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">€${option.price.toFixed(2)}</td>
           <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: center;">${totalDaysForInvoice}</td>
           <td style="padding: 14px; border-bottom: 1px solid #ddd; text-align: right; font-weight: bold;">€${(option.price * totalDaysForInvoice).toFixed(2)}</td>
         </tr>`
      );
    });
  }

  const priceTable = `
  <table style="width: 100%; border-collapse: collapse; margin-bottom: 30px; border: 2px solid ${
    colors.primary
  }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
    <thead>
      <tr style="background: ${colors.primary}; color: white;">
        <th style="padding: 15px; text-align: left; font-size: 15px;">Description</th>
        <th style="padding: 15px; text-align: center; font-size: 15px;">Rate</th>
        <th style="padding: 15px; text-align: center; font-size: 15px;">Quantity</th>
        <th style="padding: 15px; text-align: right; font-size: 15px;">Amount</th>
      </tr>
    </thead>
    <tbody>
      ${priceRows}
      ${additionalOptionsRows.join("")}
    </tbody>
  </table>
`;

  const additionalNotesSection = rental.additionalNotes
    ? `
    <div style="margin-bottom: 20px; background: #f8f8f8; padding: 15px; border-left: 4px solid #1e3a8a;">
      <h4 style="margin-bottom: 10px; color: #1e3a8a; text-transform: uppercase; font-weight: bold;">Additional Notes</h4>
      <p style="margin: 0; font-style: italic;">${rental.additionalNotes}</p>
    </div>
  `
    : "";

  const totalsSection = `
  <div style="display: flex; justify-content: flex-end;">
    <div style="width: 350px;">
      <div style="border: 2px solid ${
        colors.primary
      }; border-radius: 8px; overflow: hidden; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
        <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
          colors.accent
        };">
          <div style="display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">Subtotal:</span>
            <span>€${(rental.totalPrice / 1.24).toFixed(2)}</span>
          </div>
        </div>
        ${
          rental.discountAmount
            ? `
        <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
          colors.accent
        }; color: ${colors.primary};">
          <div style="display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">Discount:</span>
            <span>-€${rental.discountAmount.toFixed(2)}</span>
          </div>
        </div>
        `
            : ""
        }
        <div style="padding: 14px; border-bottom: 1px solid #ddd; background: ${
          colors.accent
        };">
          <div style="display: flex; justify-content: space-between;">
            <span style="font-weight: bold;">VAT (24%):</span>
            <span>€${(rental.totalPrice - rental.totalPrice / 1.24).toFixed(
              2
            )}</span>
          </div>
        </div>
        <div style="padding: 16px; background: ${
          colors.primary
        }; color: white;">
          <div style="display: flex; justify-content: space-between; font-weight: bold; font-size: 18px;">
            <span>TOTAL:</span>
            <span>€${rental.totalPrice.toFixed(2)} (NOT PAID)</span>
          </div>
        </div>
      </div>
    </div>
  </div>
`;
  const footer = `
  <div style="margin-top: 50px;">
    <div style="margin-bottom: 30px; padding: 20px; background: ${colors.accent}; border-radius: 8px; border-left: 6px solid ${colors.primary}; box-shadow: 0 3px 10px rgba(0,0,0,0.1);">
      <p style="margin: 0; font-style: italic; color: ${colors.dark};">Thank you for choosing <span style="font-weight: bold; color: ${colors.primary};">Luxury Car Mykonos Rental</span>. For assistance, contact us at <span style="font-weight: bold;">+306941419012</span> or <span style="font-weight: bold;">nleont@gmail.com</span></p>
    </div>
    <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 30px;">
      <div style="width: 30%;">
        <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
        <div style="background: ${colors.primary}; height: 2px;"></div>
      </div>
      <div style="width: 200px; text-align: center;">
        <div style="border-bottom: 2px solid ${colors.primary}; margin-bottom: 8px; padding-bottom: 15px;">
          <img src="data:image/png;base64,..." alt="Signature" style="max-width: 150px;" />
        </div>
        <p style="margin: 0; font-weight: bold; color: ${colors.primary};">Nikos Leontitsis</p>
        <p style="margin: 0; color: ${colors.dark};">Managing Director</p>
      </div>
      <div style="width: 30%;">
        <div style="background: ${colors.primary}; height: 4px; margin-bottom: 5px;"></div>
        <div style="background: ${colors.primary}; height: 2px;"></div>
      </div>
    </div>
  </div>
  <div style="margin-top: 40px; text-align: center; padding-top: 20px; border-top: 2px dashed ${colors.accent};">
  </div>
`;

  invoiceDiv.innerHTML =
    header +
    infoSection +
    priceTable +
    additionalNotesSection +
    totalsSection +
    footer;
  document.body.appendChild(invoiceDiv);

  try {
    const scale = 2;
    const canvas = await html2canvas(invoiceDiv, {
      scale: scale,
      logging: false,
      useCORS: true,
      allowTaint: true,
    });

    const imgData = canvas.toDataURL("image/jpeg", 1.0);
    const pdf = new jsPDF("p", "pt", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgWidth = canvas.width / scale;
    const imgHeight = canvas.height / scale;
    const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
    const imgX = (pdfWidth - imgWidth * ratio) / 2;
    const imgY = 20;

    pdf.addImage(
      imgData,
      "JPEG",
      imgX,
      imgY,
      imgWidth * ratio,
      imgHeight * ratio
    );

    // Add page numbers at the bottom
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(10);
      pdf.setTextColor(150);
    }

    // Instead of saving, return the PDF as base64
    const pdfData = pdf.output("datauristring");
    return pdfData.split(",")[1]; // Return just the base64 data
  } finally {
    document.body.removeChild(invoiceDiv);
  }
};

// Timezone conversion utilities for DatePicker
const convertAthensTimeToLocal = (athensDate) => {
  if (!athensDate) return null;
  
  // Create a moment in Athens timezone
  const athensMoment = moment.tz(athensDate, 'Europe/Athens');
  
  // Convert to local timezone for DatePicker display
  return athensMoment.local().toDate();
};

const convertLocalTimeToAthens = (localDate) => {
  if (!localDate) return null;
  
  // Take the local date/time values and treat them as Athens time
  return moment().tz('Europe/Athens')
    .year(localDate.getFullYear())
    .month(localDate.getMonth())
    .date(localDate.getDate())
    .hour(localDate.getHours())
    .minute(localDate.getMinutes())
    .second(0)
    .millisecond(0)
    .toDate();
};

const CarDetails = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { slug } = useParams();
  const { car: initialCar, formData: initialFormData } = location.state || {};
  const [car, setCar] = useState(
    initialCar
      ? {
          ...initialCar,
          image:
            getCarImage(initialCar?.image) || getCarImage(initialCar?.images?.[0]),
        }
      : null
  );

  const [showImportantInfo, setShowImportantInfo] = useState(false);
  const [showInsurance, setShowInsurance] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  // const [showPayment, setShowPayment] = useState(false); // Commented out for now
  const [isSubmitting, setIsSubmitting] = useState(false);
  // const [pendingRentalData, setPendingRentalData] = useState(null); // Commented out for now

  // Add invoice state
  const [wantsInvoice, setWantsInvoice] = useState(false);
  const [invoiceData, setInvoiceData] = useState({
    companyName: "",
    vatNumber: "",
    address: "",
    city: "",
    postalCode: "",
    country: "Greece"
  });

  function getCarImage(imagePath) {
    if (!imagePath) return null;

    if (typeof imagePath === "string") {
      const filename = imagePath.split("/").pop();
      return imageMap[filename] || imagePath;
    }

    return imagePath;
  }

  const [formData, setFormData] = useState({
    pickupDate: initialFormData?.dateRange?.pickup
      ? new Date(initialFormData.dateRange.pickup)
      : null,
    dropoffDate: initialFormData?.dateRange?.dropoff
      ? new Date(initialFormData.dateRange.dropoff)
      : null,
    pickupLocation: initialFormData?.originalPickupLocation || "",
    dropoffLocation: initialFormData?.originalDropoffLocation || "",
    customPickupLocation: initialFormData?.customPickupLocation || "",
    customDropoffLocation: initialFormData?.customDropoffLocation || "",
    pickupTime: initialFormData?.dateRange?.pickupTime || "",
    dropoffTime: initialFormData?.dateRange?.dropoffTime || "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    age: initialFormData?.age || "26+",
    couponCode: initialFormData?.couponCode || "",
    passengers: initialFormData?.passengers || "",
    transmission: initialFormData?.transmission || "",
    additionalOptions: {},
    notes: "",
    paymentMethod: "",
  });

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(false);
  
  const [sameLocation, setSameLocation] = useState(
    initialFormData?.originalDropoffLocation ===
      initialFormData?.originalPickupLocation
  );

  const [errors, setErrors] = useState({});
  const [totalPrice, setTotalPrice] = useState(car?.dynamicPrice || car?.basePrice || car?.price || 0);

  const defaultLocationOptions = [
    { value: "airport", label: "Airport of Mykonos (JMK)" },
    { value: "central", label: "Mykonos Central Office" },
    { value: "new-port", label: "Mykonos New Port (Tourlos)" },
    { value: "old-port", label: "Mykonos Old Port" },
    { value: "hotel", label: "Hotel / Apartment / Villa" },
    { value: "other", label: "Other Location (Hotel/Restaurant/Beach)" },
  ];
  
  const [locationOptions, setLocationOptions] = useState(defaultLocationOptions);
  const [additionalOptionsData, setAdditionalOptionsData] = useState([]);
  const [loadingAdditionalOptions, setLoadingAdditionalOptions] = useState(false);
  
  const defaultAdditionalOptions = [
    { _id: "default-child-seat", name: "Child Seat", description: "Add a Child Seat to your Vehicle!", price: 0, isEnabled: true },
    { _id: "default-baby-chair", name: "Baby Chair", description: "Add a Baby Chair to your Vehicle!", price: 0, isEnabled: true },
    { _id: "default-gps", name: "GPS", description: "Navigation system for your journey", price: 0, isEnabled: true }
  ];
  
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const response = await locations.getActive();
        
        if (response && Array.isArray(response)) {
          const apiLocations = response.map(location => ({
            value: location._id,
            label: location.name
          }));
          
          apiLocations.push(
            { value: "other", label: "Other Location (Hotel/Restaurant/Beach)" }
          );
          
          setLocationOptions(apiLocations);
        }
      } catch (error) {
        console.error("Error fetching locations:", error);
      }
    };
    
    const fetchAdditionalOptions = async () => {
      setLoadingAdditionalOptions(true);
      try {
        const options = await additionalOptions.getActive();
        
        if (options && Array.isArray(options)) {
          setAdditionalOptionsData(options);
          
          const initializedOptions = {};
          options.forEach(option => {
            initializedOptions[option._id] = false;
          });
          
          setFormData(prev => ({
            ...prev,
            additionalOptions: initializedOptions
          }));
        } else {
          setAdditionalOptionsData(defaultAdditionalOptions);
          
          const initializedOptions = {};
          defaultAdditionalOptions.forEach(option => {
            initializedOptions[option._id] = false;
          });
          
          setFormData(prev => ({
            ...prev,
            additionalOptions: initializedOptions
          }));
        }
      } catch (error) {
        console.error("Error fetching additional options:", error);
        setAdditionalOptionsData(defaultAdditionalOptions);
        
        const initializedOptions = {};
        defaultAdditionalOptions.forEach(option => {
          initializedOptions[option._id] = false;
        });
        
        setFormData(prev => ({
          ...prev,
          additionalOptions: initializedOptions
        }));
      } finally {
        setLoadingAdditionalOptions(false);
      }
    };

    fetchLocations();
    fetchAdditionalOptions();
  }, []);

  // Function to convert location ObjectId to readable name
  const getLocationNameFromId = (locationId) => {
    console.log("[CarDetails getLocationNameFromId] Called with:", locationId);
    console.log("[CarDetails getLocationNameFromId] Available locationOptions:", locationOptions);
    
    if (!locationId) {
      console.log("[CarDetails getLocationNameFromId] No locationId provided, returning empty string");
      return "";
    }

    // Handle string values that are not ObjectIds (like "airport", "central", etc.)
    if (typeof locationId === "string" && !locationId.match(/^[0-9a-fA-F]{24}$/)) {
      console.log("[CarDetails getLocationNameFromId] Non-ObjectId string detected:", locationId);
      
      // Find in location options
      const foundOption = locationOptions.find(option => option.value === locationId);
      if (foundOption) {
        console.log("[CarDetails getLocationNameFromId] Found option for non-ObjectId:", foundOption.label);
        return foundOption.label;
      }
      
      // Return as-is if it's a custom location
      console.log("[CarDetails getLocationNameFromId] Non-ObjectId not found in options, returning as-is:", locationId);
      return locationId;
    }

    // Handle ObjectId strings - convert to readable name
    const foundOption = locationOptions.find(option => option.value === locationId);
    if (foundOption) {
      console.log("[CarDetails getLocationNameFromId] Found option for ObjectId:", foundOption.label);
      return foundOption.label;
    }

    console.log("[CarDetails getLocationNameFromId] ObjectId not found in options, returning as-is:", locationId);
    return locationId;
  };

  const [datePickerOpen, setDatePickerOpen] = useState({
    pickup: false,
    dropoff: false,
  });

  const [dateTimeComplete, setDateTimeComplete] = useState({
    pickup: false,
    dropoff: false,
  });

  const [timeWarnings, setTimeWarnings] = useState({
    pickup: false,
    dropoff: false,
  });

  const [availabilityData, setAvailabilityData] = useState(null);
  const [durationDiscounts, setDurationDiscounts] = useState({
    shortTerm: { discount: 0 },
    mediumTerm: { discount: 5 },
    longTerm: { discount: 10 },
  });

  useEffect(() => {
    if (!car && slug) {
      cars.getAll().then(response => {
        const found = response.data.find(c => slugifyCar(c) === slug);
        if (found) {
          setCar({
            ...found,
            image: getCarImage(found?.image) || getCarImage(found?.images?.[0]),
          });
          console.log("Car found:", found);
        }
      })};
  }, [car, slug]);

  useEffect(() => {
    if (car) {
      if (formData.pickupDate && formData.dropoffDate) {
        if (availabilityData?.dynamicPrices?.length) {
          const avgPrice = getAveragePriceForDateRange();
          setTotalPrice(avgPrice || car.dynamicPrice || car.basePrice || car.price || 0);
        } else {
          setTotalPrice(car.dynamicPrice || car.basePrice || car.price || 0);
        }
      } else {
        setTotalPrice(car.dynamicPrice || car.basePrice || car.price || 0);
      }
    }
  }, [car, availabilityData, formData.pickupDate, formData.dropoffDate]);

  // Keep all your existing utility functions exactly as they are
  const fetchPricingForDateRange = async (startDate, endDate) => {
    try {
      if (!car?.category || !car?.stringId) return;
      
      const start = new Date(startDate);
      const end = new Date(endDate);
      
      const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
      const monthEnd = new Date(end.getFullYear(), end.getMonth() + 1, 0);
      
      const response = await cars.getAvailable({
        startDate: monthStart.toISOString(),
        endDate: monthEnd.toISOString(),
        category: car?.category,
        includeAll: true,
      });

      const carData = response.data.find((c) => c.stringId === car?.stringId);
      
      if (carData?.availability) {
        setAvailabilityData(prevData => {
          if (prevData?.dynamicPrices?.length) {
            const existingDatesMap = {};
            prevData.dynamicPrices.forEach(item => {
              const dateStr = new Date(item.date).toISOString().split('T')[0];
              existingDatesMap[dateStr] = true;
            });
            
            const newPrices = carData.availability.filter(item => {
              const dateStr = new Date(item.date).toISOString().split('T')[0];
              return !existingDatesMap[dateStr];
            });
            
            return {
              dynamicPrices: [...prevData.dynamicPrices, ...newPrices]
            };
          }
          
          return {
            dynamicPrices: carData.availability || []
          };
        });
        
        return true;
      }
      
      return false;
    } catch (error) {
      console.error("Error fetching pricing data:", error);
      return false;
    }
  };
  
  useEffect(() => {
    const fetchInitialPricing = async () => {
      try {
        if (formData.pickupDate && formData.dropoffDate) {
          await fetchPricingForDateRange(formData.pickupDate, formData.dropoffDate);
        } else {
          const today = new Date();
          const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
          const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 0);
          
          const response = await cars.getAvailable({
            startDate: monthStart.toISOString(),
            endDate: monthEnd.toISOString(),
            category: car?.category,
            includeAll: true,
          });

          const carData = response.data.find((c) => c.stringId === car?.stringId);
          
          setAvailabilityData({
            dynamicPrices: carData?.availability || [],
          });
        }

        if (car) {
          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];
          
          const currentAvailabilityData = availabilityData?.dynamicPrices || [];
          
          const todayPricing = currentAvailabilityData.find(
            a => new Date(a.date).toISOString().split('T')[0] === todayStr
          );
          
          if (todayPricing && todayPricing.dynamicPrice) {
            setCar(prev => ({
              ...prev,
              dynamicPrice: todayPricing.dynamicPrice
            }));
          }
        }
      } catch (error) {
        console.error("Error fetching initial pricing:", error);
      }
    };

    fetchInitialPricing();
  }, [car?.category, car?.stringId]);

  useEffect(() => {
    const fetchDurationDiscounts = async () => {
      try {
        const response = await rentals.getDurationDiscounts();
        setDurationDiscounts(response.data);
      } catch (error) {
        console.error("Error fetching duration discounts:", error);
      }
    };
    fetchDurationDiscounts();
  }, []);

  // Fetch payment methods on component mount
  useEffect(() => {
    const fetchPaymentMethods = async () => {
      setLoadingPaymentMethods(true);
      try {
        const response = await oxygen.getPaymentMethods();
        
        if (response && response.data) {
          setPaymentMethods(response.data);
        } else {
          console.error("Invalid payment methods response:", response);
          toast.error("Failed to load payment methods");
        }
      } catch (error) {
        console.error("Error fetching payment methods:", error);
        toast.error("Failed to load payment methods");
      } finally {
        setLoadingPaymentMethods(false);
      }
    };
    fetchPaymentMethods();
  }, []);
  
  const handleMonthChange = async (date) => {
    try {
      const monthStart = new Date(date.getFullYear(), date.getMonth(), 1);
      const monthEnd = new Date(date.getFullYear(), date.getMonth() + 1, 0);
      
      await fetchPricingForDateRange(monthStart, monthEnd);
      
      const today = new Date();
      if (date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear()) {
        const todayStr = today.toISOString().split('T')[0];
        
        const currentAvailabilityData = availabilityData?.dynamicPrices || [];
        
        const todayPricing = currentAvailabilityData.find(
          a => new Date(a.date).toISOString().split('T')[0] === todayStr
        );
        
        if (todayPricing && todayPricing.dynamicPrice) {
          setCar(prev => ({
            ...prev,
            dynamicPrice: todayPricing.dynamicPrice
          }));
          
          if (!formData.pickupDate || !formData.dropoffDate) {
            setTotalPrice(todayPricing.dynamicPrice);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching pricing for month:", error);
      setAvailabilityData((prev) => prev || { dynamicPrices: [] });
    }
  };

  const handleDateChange = async (date, type) => {
    const isPickup = type === "pickupDate";
    const stateKey = isPickup ? "pickup" : "dropoff";

    const hasTime = date && date.getHours() !== 0;

    // Validate pickup time if it's today
    if (isPickup && date) {
      const nowInAthens = moment().tz('Europe/Athens');
      const todayInAthens = moment().tz('Europe/Athens').startOf('day');
      
      // Convert the selected date to Athens timezone - treating the local date/time as if it were Athens time
      const selectedDateInAthens = moment().tz('Europe/Athens')
        .year(date.getFullYear())
        .month(date.getMonth())
        .date(date.getDate())
        .hour(date.getHours())
        .minute(date.getMinutes())
        .second(0)
        .millisecond(0);
      
      const selectedDayInAthens = selectedDateInAthens.clone().startOf('day');
      
      // If pickup date is today (in Athens time), check if time is at least 1 hour ahead
      if (selectedDayInAthens.isSame(todayInAthens) && hasTime) {
        const minimumTimeInAthens = nowInAthens.clone().add(1, 'hour'); // 1 hour from now in Athens
        
        if (selectedDateInAthens.isBefore(minimumTimeInAthens)) {
          setTimeWarnings((prev) => ({
            ...prev,
            [stateKey]: true,
          }));
          
          setErrors((prev) => ({
            ...prev,
            pickupDate: "Pickup time must be at least 1 hour from now for same-day bookings (Athens time)"
          }));
          
          setDateTimeComplete((prev) => ({
            ...prev,
            [stateKey]: false,
          }));
          
          setFormData((prev) => ({
            ...prev,
            [type]: date,
          }));
          return;
        }
      }
    }

    // Clear any previous time-related errors for valid times
    if (isPickup && hasTime) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        if (newErrors.pickupDate?.includes("must be at least 1 hour from now")) {
          delete newErrors.pickupDate;
        }
        return newErrors;
      });
    }

    setDateTimeComplete((prev) => ({
      ...prev,
      [stateKey]: hasTime,
    }));

    setTimeWarnings((prev) => ({
      ...prev,
      [stateKey]: !hasTime,
    }));

    setFormData((prev) => ({
      ...prev,
      [type]: date,
    }));

    if (hasTime) {
      setDatePickerOpen((prev) => ({
        ...prev,
        [stateKey]: false,
        ...(isPickup ? { dropoff: true } : {}),
      }));
    }
  };

  const renderDayContents = (day, date) => {
    const dayDate = new Date(date);
    const isPickerOpen = datePickerOpen.pickup || datePickerOpen.dropoff;

    const dynamicPriceData = availabilityData?.dynamicPrices?.find(
      (p) => new Date(p.date).toDateString() === dayDate.toDateString()
    );

    const dayPrice = dynamicPriceData?.dynamicPrice || car?.basePrice;

    return (
      <div className="relative py-4 mx-auto text-center">
        <span>{dayDate.getDate()}</span>
        {dayPrice && (
          <div
            className={`absolute bottom-0 left-0 right-0 text-[10px] ${
              dynamicPriceData ? "text-red-600" : "text-green-600"
            } font-bold`}
          >
            €{dayPrice}
          </div>
        )}
      </div>
    );
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.pickupDate) {
      newErrors.pickupDate = "Pick-up date and time are required";
    } else {
      // Additional validation for pickup time
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const pickupDate = new Date(formData.pickupDate);
      const pickupDateOnly = new Date(pickupDate);
      pickupDateOnly.setHours(0, 0, 0, 0);
      
      // If pickup date is today, check if time is at least 1 hour ahead
      if (pickupDateOnly.getTime() === today.getTime()) {
        const minimumTime = new Date(now.getTime() + 60 * 60 * 1000); // 1 hour from now
        
        if (pickupDate < minimumTime) {
          newErrors.pickupDate = "Pickup time must be at least 1 hour from now for same-day bookings";
        }
      }
    }

    if (!formData.dropoffDate)
      newErrors.dropoffDate = "Drop-off date and time are required";
    if (!formData.firstName) newErrors.firstName = "First name is required";
    if (!formData.lastName) newErrors.lastName = "Last name is required";
    
    // Enhanced phone validation
    if (!formData.phone) {
      newErrors.phone = "Phone number is required";
    } else {
      // Remove all non-digit characters to check actual number length
      const phoneDigits = formData.phone.replace(/\D/g, '');
      
      // Check if phone is just the country code or too short
      if (phoneDigits.length < 10) {
        newErrors.phone = "Please enter a complete phone number";
      } else if (formData.phone === "+30" || formData.phone.replace(/\s/g, '') === "+30") {
        newErrors.phone = "Please enter your phone number after the country code";
      } else if (!/^\+[1-9]\d{1,14}$/.test(formData.phone.replace(/\s/g, ''))) {
        newErrors.phone = "Please enter a valid international phone number";
      }
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }

    if (!formData.pickupLocation) {
      newErrors.pickupLocation = "Pick-up location is required";
    } else if (
      formData.pickupLocation === "other" &&
      !formData.customPickupLocation
    ) {
      newErrors.pickupLocation = "Please enter the custom pick-up location";
    }

    if (!sameLocation) {
      if (!formData.dropoffLocation) {
        newErrors.dropoffLocation = "Drop-off location is required";
      } else if (
        formData.dropoffLocation === "other" &&
        !formData.customDropoffLocation
      ) {
        newErrors.dropoffLocation = "Please enter the custom drop-off location";
      }
    }

    // Invoice validation
    if (wantsInvoice) {
      if (!invoiceData.companyName) {
        newErrors.companyName = "Company name is required for invoice";
      }
      if (!invoiceData.vatNumber) {
        newErrors.vatNumber = "VAT number is required for invoice";
      }
      if (!invoiceData.address) {
        newErrors.address = "Address is required for invoice";
      }
      if (!invoiceData.city) {
        newErrors.city = "City is required for invoice";
      }
      if (!invoiceData.postalCode) {
        newErrors.postalCode = "Postal code is required for invoice";
      }
    }

    if (formData.pickupDate && formData.dropoffDate) {
      if (formData.pickupDate >= formData.dropoffDate) {
        newErrors.dropoffDate = "Drop-off time must be after pick-up time";
      }
    }

    // Payment method validation
    if (!formData.paymentMethod) {
      newErrors.paymentMethod = "Please select a payment method";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const formatDateRange = (date) => {
    return date.toLocaleDateString("el-GR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const getPickupDatePrice = () => {
    if (!formData.pickupDate || !availabilityData?.dynamicPrices || !availabilityData.dynamicPrices.length) {
      return null;
    }

    try {
      const pickupDateString = formData.pickupDate.toDateString();
      const dynamicPriceData = availabilityData.dynamicPrices.find(
        (p) => new Date(p.date).toDateString() === pickupDateString
      );
      
      if (dynamicPriceData && dynamicPriceData.dynamicPrice) {
        return dynamicPriceData.dynamicPrice;
      }
      
      return null;
    } catch (error) {
      console.error("Error getting pickup date price:", error);
      return null;
    }
  };
  
  const getAveragePriceForDateRange = () => {
    if (!formData.pickupDate || !formData.dropoffDate) {
      return null;
    }

    try {
      const breakdown = calculatePriceBreakdown();
      if (!breakdown || breakdown.length === 0) {
        return car?.dynamicPrice || car?.basePrice;
      }
      
      let totalAmount = 0;
      let totalDays = 0;
      
      breakdown.forEach(period => {
        totalAmount += period.price * period.days;
        totalDays += period.days;
      });
      
      if (totalDays > 0) {
        return parseFloat((totalAmount / totalDays).toFixed(0));
      }
      
      return car?.dynamicPrice || car?.basePrice;
    } catch (error) {
      console.error("Error calculating average price:", error);
      return car?.dynamicPrice || car?.basePrice;
    }
  };

  const calculateDurationIn24HourDays = (pickupDateStr, dropoffDateStr) => {
    if (!pickupDateStr || !dropoffDateStr) {
      return 0;
    }

    const pickupDate = new Date(pickupDateStr);
    const dropoffDate = new Date(dropoffDateStr);

    if (isNaN(pickupDate.getTime()) || isNaN(dropoffDate.getTime())) {
      console.error("Invalid date provided for duration calculation.");
      return 0;
    }

    if (dropoffDate <= pickupDate) {
      if (dropoffDate.getTime() === pickupDate.getTime()) {
        return 0; 
      }
      if (dropoffDate < pickupDate) {
        return 0;
      }
    }

    const diffTime = dropoffDate.getTime() - pickupDate.getTime();
    
    if (diffTime < 0) {
      return 0;
    }

    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    
    const pickupMinutes = pickupDate.getHours() * 60 + pickupDate.getMinutes();
    const dropoffMinutes = dropoffDate.getHours() * 60 + dropoffDate.getMinutes();
    
    const fullDays = Math.floor(diffTime / millisecondsPerDay);
    
    let extraDay = 0;
    
    if (dropoffMinutes > pickupMinutes) {
      extraDay = 1;
    } else if (dropoffMinutes === pickupMinutes) {
      const pickupSeconds = pickupDate.getSeconds();
      const dropoffSeconds = dropoffDate.getSeconds();
      if (dropoffSeconds > pickupSeconds) {
        extraDay = 1;
      }
    }
    
    return fullDays + extraDay;
  };

  const calculatePriceBreakdown = () => {
    if (!formData.pickupDate || !formData.dropoffDate) {
      return null;
    }

    const breakdown = [];
    let currentDate = new Date(formData.pickupDate);
    const endDate = new Date(formData.dropoffDate);
    
    const exactPickupDate = new Date(currentDate);
    const exactDropoffDate = new Date(endDate);
    
    endDate.setHours(23, 59, 59);

    let loopCurrentDate = new Date(currentDate);
    loopCurrentDate.setHours(0, 0, 0, 0);
    
    while (loopCurrentDate <= endDate) {
      const dateString = loopCurrentDate.toDateString();

      const dynamicPriceData = availabilityData?.dynamicPrices?.find(
        (p) => new Date(p.date).toDateString() === dateString
      );

      const price = dynamicPriceData?.dynamicPrice || car?.basePrice;

      const lastEntry = breakdown[breakdown.length - 1];
      if (lastEntry && lastEntry.price === price) {
        lastEntry.days++;
        lastEntry.endDate = new Date(loopCurrentDate);
      } else {
        breakdown.push({
          startDate: new Date(loopCurrentDate),
          endDate: new Date(loopCurrentDate),
          price,
          days: 1,
        });
      }

      loopCurrentDate = new Date(loopCurrentDate.setDate(loopCurrentDate.getDate() + 1));
    }
    
    if (breakdown.length > 0) {
      const lastPeriod = breakdown[breakdown.length - 1];
      const lastDayStart = new Date(lastPeriod.endDate);
      lastDayStart.setHours(0, 0, 0, 0);
      
      if (lastDayStart.toDateString() === exactDropoffDate.toDateString()) {
        const pickupTime = exactPickupDate.getHours() * 60 + exactPickupDate.getMinutes();
        const dropoffTime = exactDropoffDate.getHours() * 60 + exactDropoffDate.getMinutes();
        
        if (dropoffTime <= pickupTime) {
          if (lastPeriod.days === 1) {
            breakdown.pop();
          } else {
            lastPeriod.days--;
            const newEndDate = new Date(lastPeriod.endDate);
            newEndDate.setDate(newEndDate.getDate() - 1);
            lastPeriod.endDate = newEndDate;
          }
        }
      }
    }

    return breakdown;
  };

  const calculateTotalPrice = useCallback(() => {
    if (!formData.pickupDate || !formData.dropoffDate) {
      return car?.basePrice || 0;
    }

    const breakdown = calculatePriceBreakdown();
    if (!breakdown) return car?.basePrice || 0;

    const subtotal = breakdown.reduce(
      (total, period) => total + period.price * period.days,
      0
    );

    const totalDays = calculateDurationIn24HourDays(formData.pickupDate, formData.dropoffDate);

    let discountPercentage = 0;
    if (totalDays > 10) {
      discountPercentage = durationDiscounts.longTerm.discount;
    } else if (totalDays > 3) {
      discountPercentage = durationDiscounts.mediumTerm.discount;
    } else {
      discountPercentage = durationDiscounts.shortTerm.discount;
    }

    const discount = (subtotal * discountPercentage) / 100;
    
    let additionalOptionsCost = 0;
    if (additionalOptionsData.length > 0 && formData.additionalOptions) {
      additionalOptionsData.forEach(option => {
        if (formData.additionalOptions[option._id]) {
          additionalOptionsCost += option.price * totalDays;
        }
      });
    }
    const totalPrice = subtotal - discount + additionalOptionsCost;
    return parseFloat(totalPrice.toFixed(2));
  }, [formData, car, availabilityData, durationDiscounts, additionalOptionsData]);


    const [couponStatus, setCouponStatus] = useState({
      isValid: false,
      discount: 0,
      message: "",
    });

    const validateCoupon = async (code) => {
      if (!code) {
        setCouponStatus({ isValid: false, discount: 0, message: "" });
        return;
      }

      const basePriceForCoupon = calculateTotalPrice(); 
      const netBasePriceForCoupon = basePriceForCoupon / 1.24;

      try {
        const response = await coupons.validate(code, netBasePriceForCoupon); 
        setCouponStatus({
          isValid: true,
          discount: response.data.discount,
          message: "✓ Coupon applied successfully!",
        });
      } catch (error) {
        setCouponStatus({
          isValid: false,
          discount: 0,
          message: "✕ Invalid coupon code",
        });
      }
    };

  const handleSubmit = async (e) => {
      e.preventDefault();
      
      // Show closure message instead of processing booking
      Swal.fire({
        icon: "info",
        title: "We're Closed for the Year",
        html: `
          <div class="text-center">
            <p class="text-lg mb-3">🏖️ Thank you for your interest! 🏖️</p>
            <p class="mb-2">We're currently closed for the 2024-2025 season.</p>
            <p class="text-sm text-gray-600">We'll be back with our amazing fleet next summer!</p>
            <p class="text-lg mt-3">See you in 2026! ☀️</p>
          </div>
        `,
        confirmButtonText: "Understood",
        confirmButtonColor: "#D81919",
      });
      
      return; // Exit early, don't process the booking
      
      if (!validateForm()) {
        const firstError = document.querySelector('[data-error="true"]');
        if (firstError) {
          firstError.scrollIntoView({ behavior: "smooth", block: "center" });
        }
        return;
      }
      
      setIsSubmitting(true);

      // Debug log raw location values from formData
      console.log("[CarDetails handleSubmit] Raw pickup location from formData:", formData.pickupLocation);
      console.log("[CarDetails handleSubmit] Raw dropoff location from formData:", formData.dropoffLocation);
      console.log("[CarDetails handleSubmit] Custom pickup location:", formData.customPickupLocation);
      console.log("[CarDetails handleSubmit] Custom dropoff location:", formData.customDropoffLocation);
      console.log("[CarDetails handleSubmit] Same location setting:", sameLocation);

      const effectivePickupLocation =
        formData.pickupLocation === "other"
          ? formData.customPickupLocation
          : getLocationNameFromId(formData.pickupLocation);
      const effectiveDropoffLocation = sameLocation
        ? formData.pickupLocation === "other"
          ? formData.customPickupLocation
          : getLocationNameFromId(formData.pickupLocation)
        : formData.dropoffLocation === "other"
        ? formData.customDropoffLocation
        : getLocationNameFromId(formData.dropoffLocation);

      // Debug log effective location names being sent to server
      console.log("[CarDetails handleSubmit] Effective pickup location name:", effectivePickupLocation);
      console.log("[CarDetails handleSubmit] Effective dropoff location name:", effectiveDropoffLocation);

      const finalPrice = calculateTotalPrice();
      const priceBreakdown = calculatePriceBreakdown()?.map((period) => ({
        startDate: period.startDate.toISOString(),
        endDate: period.endDate.toISOString(),
        pricePerDay: period.price,
        numberOfDays: period.days,
        totalForPeriod: period.price * period.days,
      }));

      try {
        const priceForCouponValidation = calculateTotalPrice();
        const netPriceForCouponValidation = priceForCouponValidation / 1.24;
        
        let serverValidatedDiscountAmount = 0;
        if (formData.couponCode) {
          try {
            const couponResponse = await coupons.validate(
              formData.couponCode,
              netPriceForCouponValidation 
            );
            serverValidatedDiscountAmount = couponResponse.data.discount;
          } catch (error) {
            Swal.fire({
              icon: "error",
              title: "Invalid Coupon",
              text: "The coupon code you entered is not valid or does not apply.",
            });
            setIsSubmitting(false);
            return;
          }
        }

        const netPriceBeforeCoupon = priceForCouponValidation / 1.24;
        const actualDiscountToApply = Math.min(serverValidatedDiscountAmount, netPriceBeforeCoupon);
        const netPriceAfterDiscount = netPriceBeforeCoupon - actualDiscountToApply;
        const finalTotalForSubmission = netPriceAfterDiscount * 1.24;

        const totalRentalDays = calculateDurationIn24HourDays(formData.pickupDate, formData.dropoffDate);
        const processedAdditionalOptions = [];
        if (additionalOptionsData.length > 0 && formData.additionalOptions) {
          additionalOptionsData.forEach(option => {
            if (formData.additionalOptions[option._id]) {
              processedAdditionalOptions.push({
                _id: option._id,
                name: option.name,
                price: option.price,
              });
            }
          });
        }
          const rentalData = {
          car: car?.stringId,
          carName: car?.name || `${car?.make} ${car?.model}${car?.transmission ? ` (${car?.transmission})` : ''}`,
          customer: {
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            phoneNumber: formData.phone,
            age: formData.age,
          },
          pickupLocation: effectivePickupLocation,
          returnLocation: effectiveDropoffLocation,
          pickupDateTime: moment.tz({
            year: formData.pickupDate.getFullYear(),
            month: formData.pickupDate.getMonth(),
            date: formData.pickupDate.getDate(),
            hour: formData.pickupDate.getHours(),
            minute: formData.pickupDate.getMinutes()
          }, 'Europe/Athens').utc().toISOString(),
          returnDateTime: moment.tz({
            year: formData.dropoffDate.getFullYear(),
            month: formData.dropoffDate.getMonth(),
            date: formData.dropoffDate.getDate(),
            hour: formData.dropoffDate.getHours(),
            minute: formData.dropoffDate.getMinutes()
          }, 'Europe/Athens').utc().toISOString(),
          additionalOptions: processedAdditionalOptions,
          couponCode: formData.couponCode || undefined,
          basePrice: car?.basePrice,
          totalPrice: finalTotalForSubmission,
          discountAmount: actualDiscountToApply,
          priceBreakdown,
          additionalNotes: formData.notes,
          totalRentalDays: totalRentalDays,
          invoiceType: wantsInvoice ? 'invoice' : 'receipt',
          paymentMethod: formData.paymentMethod, // Make sure payment method is included
          ...(wantsInvoice && { invoiceData })
        };

        // Debug log to verify payment method is included
        console.log("[CarDetails handleSubmit] Payment method being sent:", formData.paymentMethod);
        console.log("[CarDetails handleSubmit] Full rental data:", rentalData);

        const pdfData = await generateInvoice(rentalData);
        rentalData.invoicePdf = pdfData;

        // STRIPE PAYMENT LOGIC COMMENTED OUT FOR NOW
        /*
        // Check if payment method is Stripe (credit card)
        if (formData.paymentMethod === 'stripe') {
          // For Stripe payments, create the rental first, then show payment modal
          const response = await rentals.create(rentalData);
          
          // Store rental data for payment processing
          setPendingRentalData({
            ...response.data,
            totalPrice: finalTotalForSubmission,
          });
          
          // Show payment modal
          setShowPayment(true);
        } else {
          // For other payment methods, process normally
          const response = await rentals.create(rentalData);
          setShowSuccess(true);
        }
        */
        
        // For now, process all payments normally
        const response = await rentals.create(rentalData);
        setShowSuccess(true);
      } catch (error) {
        console.error("Error submitting rental:", error);
        Swal.fire({
          icon: "error",
          title: "Booking Failed",
          text: "Failed to submit rental request. Please try again.",
        });
      } finally {
        setIsSubmitting(false);
      }
    };

    // STRIPE PAYMENT HANDLERS COMMENTED OUT FOR NOW
    /*
    // Payment success handler
    const handlePaymentSuccess = (paymentData) => {
      console.log('Payment successful:', paymentData);
      setShowPayment(false);
      setPendingRentalData(null);
      setShowSuccess(true);
    };

    // Payment error handler
    const handlePaymentError = (errorMessage) => {
      console.error('Payment failed:', errorMessage);
      Swal.fire({
        icon: "error",
        title: "Payment Failed",
        text: errorMessage || "Payment could not be processed. Please try again.",
      });
    };

    // Payment modal close handler
    const handlePaymentModalClose = () => {
      setShowPayment(false);
      setPendingRentalData(null);
    };
    */

    const getEngineCapacity = (features) => {
      const engineFeature = features?.find((f) => f.includes("Engine"));
      return engineFeature ? engineFeature.split(" ")[0] : "-";
    };

    const getFuelTank = (features) => {
      const fuelFeature = features?.find((f) => f.includes("Fuel Tank"));
      return fuelFeature ? fuelFeature : "-";
    };

    useEffect(() => {
      const priceBeforeCouponAndVAT = calculateTotalPrice();
      const netBeforeCoupon = priceBeforeCouponAndVAT / 1.24;
      
      const couponDiscountValue = couponStatus.isValid ? couponStatus.discount : 0;
      const actualCouponDiscount = Math.min(couponDiscountValue, netBeforeCoupon);
      
      const netAfterCoupon = netBeforeCoupon - actualCouponDiscount;
      const finalTotalWithVAT = netAfterCoupon * 1.24;
      
      setTotalPrice(finalTotalWithVAT);
      
      if (formData.pickupDate) {
        const newMonth = formData.pickupDate.getMonth();
        const currentMonth = new Date().getMonth();
        
        if (newMonth !== currentMonth) {
          handleMonthChange(formData.pickupDate);
        }
      }
      
      if (formData.dropoffDate) {
        const dropoffMonth = formData.dropoffDate.getMonth();
        const pickupMonth = formData.pickupDate ? formData.pickupDate.getMonth() : -1;
        
        if (pickupMonth !== -1 && pickupMonth !== dropoffMonth) {
          handleMonthChange(formData.dropoffDate);
        }
      }
    }, [calculateTotalPrice, couponStatus]);

    const [isPartner, setIsPartner] = useState(false);
    const [partnerId, setPartnerId] = useState(null);
    const [partnerCommission, setPartnerCommission] = useState(0);

    useEffect(() => {
      const checkPartnerStatus = async () => {
        const partnerToken = localStorage.getItem("partnerToken");
        if (partnerToken) {
          try {
            const response = await auth.getCurrentUser();
            if (response.data.user.isProfessionalPartner) {
              setIsPartner(true);
              setPartnerId(response.data.user._id);
              setPartnerCommission(response.data.user.commissionRate);
            }
          } catch (error) {
            console.error("Error checking partner status:", error);
          }
        }
      };
      checkPartnerStatus();
    }, []);

    // Default car description
    const defaultDescription = `Experience luxury and comfort with the ${car?.make} ${car?.model}. This ${car?.category?.toLowerCase()} vehicle offers exceptional performance and reliability for your Mykonos adventure. Featuring ${car?.transmission} transmission, ${car?.fuelType} engine, and seating for ${car?.seats} passengers, it's perfect for exploring the beautiful island of Mykonos.`;

    const title = car 
      ? `${car?.make} ${car?.model} (${car?.transmission}) • Luxury Car Mykonos` 
      : 'Luxury Car Rentals in Mykonos • Luxury Car Mykonos';
        
    const description = car 
      ? car?.description || defaultDescription
      : 'Discover our fleet of luxury cars, motos and private transfers on Luxury Car Mykonos. Great rates, no hidden fees.';
        
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://mykonosrentacars.com';
    const currentUrl = `${origin}/car-details/${slug}`;
    const imageUrl = car?.image || `${origin}/og-homepage.jpg`;
    const imageAlt = car ? `${car.make} ${car.model} - Luxury Car Mykonos` : 'Luxury Car Mykonos fleet';

    const structuredData = car ? {
      "@context": "https://schema.org/",
      "@type": "Product",
      "name": `${car.make} ${car.model} (${car.transmission})`,
      "image": [imageUrl],
      "description": description,
      "sku": car.stringId || slug,
      "brand": {
        "@type": "Brand",
        "name": car.make
      },
      "offers": {
        "@type": "Offer",
        "url": currentUrl,
        "priceCurrency": "EUR",
        "price": car.basePrice,
        "availability": "https://schema.org/InStock"
      }
    } : null;

    if (!car) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
          <div className="text-center">
            <LoadingSpinner size="lg" />
            <p className="mt-4 text-lg text-gray-600">Loading vehicle details...</p>
          </div>
        </div>
      );
    }

    return (
      <>
        <SEO
          title={title}
          description={description}
          canonicalUrl={currentUrl}
          imageUrl={imageUrl}
          imageAlt={imageAlt}
          structuredData={structuredData}
        />


        <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
          <AnimatePresence>
            {isSubmitting && (
              <motion.div 
                className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center backdrop-blur-sm"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <motion.div 
                  className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md mx-4"
                  initial={{ scale: 0.8, y: 20 }}
                  animate={{ scale: 1, y: 0 }}
                  exit={{ scale: 0.8, y: 20 }}
                >
                  <LoadingSpinner size="lg" />
                  <h3 className="mt-4 text-xl font-bold text-gray-800">Processing Your Booking</h3>
                  <p className="mt-2 text-gray-600">Please don't close this window</p>
                  <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                    <motion.div 
                      className="bg-red-500 h-2 rounded-full"
                      initial={{ width: "0%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 3, ease: "easeInOut" }}
                    />
                  </div>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>

          <Header />
          
          {/* Closed for the Year Announcement */}
          <motion.div 
            className="bg-gradient-to-r from-red-600 to-red-700 text-white py-4 sm:py-6"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-6 text-center">
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.4, delay: 0.2 }}
              >
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-2">
                  🏖️ We're Closed for the Year 2024-2025 🏖️
                </h2>
                <p className="text-base sm:text-lg opacity-90">
                  Thank you for your interest! We'll be back with our amazing fleet next summer.
                </p>
                <p className="text-sm sm:text-base opacity-80 mt-1">
                  See you in 2026! ☀️
                </p>
              </motion.div>
            </div>
          </motion.div>
          
          <main className="mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-8 max-w-7xl">
            <motion.div 
              className="flex flex-col xl:flex-row gap-4 sm:gap-6 lg:gap-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {/* Left Column - Enhanced Car Details */}
              <motion.div 
                className="xl:w-3/4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
                  {/* Navigation Header */}
                  <div className="bg-gradient-to-r from-red-600 to-red-700 p-4 sm:p-6">
                    <div className="flex justify-between items-center flex-wrap gap-2">
                      <motion.button
                        onClick={() => {
                          try {
                            if (window.history.length > 1) {
                              navigate(-1);
                            } else {
                              navigate('/search');
                            }
                          } catch (e) {
                            navigate('/search');
                          }
                        }}
                        className="flex items-center text-white hover:text-red-200 transition-colors duration-300 group"
                        whileHover={{ x: -5 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaArrowLeft className="mr-2 sm:mr-3 group-hover:animate-pulse" />
                        <span className="font-medium text-sm sm:text-base">Back to Search</span>
                      </motion.button>

                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                        <motion.button
                          onClick={() => setShowImportantInfo(true)}
                          className="flex items-center text-white hover:text-red-200 transition-colors duration-300 group text-sm sm:text-base"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FaInfoCircle className="mr-1 sm:mr-2 group-hover:animate-bounce" />
                          <span>Info</span>
                        </motion.button>
                        <motion.button
                          onClick={() => setShowInsurance(true)}
                          className="flex items-center text-white hover:text-red-200 transition-colors duration-300 group text-sm sm:text-base"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          <FaShieldAlt className="mr-1 sm:mr-2 group-hover:animate-bounce" />
                          <span>Insurance</span>
                        </motion.button>
                      </div>
                    </div>
                  </div>

                  {/* Hero Section with Car Image */}
                  <div className="relative overflow-hidden bg-gradient-to-br from-gray-50 to-white">
                    <motion.div 
                      className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 p-4 sm:p-6 lg:p-8"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <div className="lg:w-3/5 relative">
                        <motion.div
                          className="relative group"
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.3 }}
                        >
                          <img
                            src={car?.image}
                            alt={`${car?.make} ${car?.model}`}
                            className="w-full h-auto object-contain rounded-xl sm:rounded-2xl shadow-lg group-hover:shadow-2xl transition-shadow duration-300"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-xl sm:rounded-2xl" />
                        </motion.div>
                        
                        {/* Floating Category Badge */}
                        <motion.div 
                          className="absolute top-2 left-2 sm:top-4 sm:left-4 bg-gradient-to-r from-red-600 to-red-700 text-white px-2 py-1 sm:px-4 sm:py-2 rounded-full font-semibold shadow-lg text-xs sm:text-sm"
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5, type: "spring" }}
                        >
                          {car?.category}
                        </motion.div>
                      </div>

                      <div className="lg:w-2/5 space-y-4 sm:space-y-6">
                        <motion.div
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.3 }}
                        >
                          <h1 className="text-4xl font-bold text-gray-900 mb-2">
                            {car?.make} {car?.model}
                          </h1>
                          <p className="text-xl text-gray-600 mb-6">
                            {car?.year} • {car?.transmission?.charAt(0).toUpperCase() + car?.transmission?.slice(1)}
                          </p>
                        </motion.div>

                        {/* Key Specifications Grid */}
                        <motion.div 
                          className="grid grid-cols-2 gap-4"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.4 }}
                        >
                          <SpecCard 
                            icon={FaUsers} 
                            label="Passengers" 
                            value={car?.seats || car?.persons} 
                            highlight={true}
                          />
                          <SpecCard 
                            icon={FaDoorOpen} 
                            label="Doors" 
                            value={car?.doors} 
                          />
                          <SpecCard 
                            icon={FaSuitcase} 
                            label="Luggage" 
                            value={car?.suitcases} 
                          />
                          <SpecCard 
                            icon={FaGasPump} 
                            label="Fuel Type" 
                            value={car?.fuelType?.charAt(0).toUpperCase() + car?.fuelType?.slice(1)} 
                          />
                        </motion.div>

                        {/* Rating and Reviews */}
                        {/* <motion.div 
                          className="flex items-center space-x-2 p-4 bg-yellow-50 rounded-xl border border-yellow-200"
                          initial={{ opacity: 0, scale: 0.9 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: 0.5 }}
                        >
                          <div className="flex space-x-1">
                            {[...Array(5)].map((_, i) => (
                              <FaStar key={i} className="text-yellow-400 text-lg" />
                            ))}
                          </div>
                          <span className="font-semibold text-gray-800">4.9</span>
                          <span className="text-gray-600">(127 reviews)</span>
                        </motion.div> */}
                      </div>
                    </motion.div>
                  </div>

                  {/* Detailed Specifications */}
                  <motion.div 
                    className="p-4 sm:p-6 lg:p-8 border-t border-gray-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6 }}
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                      <FaCarSide className="mr-2 sm:mr-3 text-red-600" />
                      Vehicle Specifications
                    </h2>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
                      {[
                        { label: "Exterior Color", value: car?.exteriorColor },
                        { label: "Interior Color", value: car?.interiorColor },
                        { label: "Engine", value: car?.specifications?.engine || getEngineCapacity(car?.features) },
                        { label: "Power", value: car?.specifications?.power },
                        { label: "Consumption", value: car?.specifications?.consumption },
                        { label: "Drivetrain", value: car?.drivetrain || "2WD" },
                        { label: "Air Conditioning", value: car?.airConditioner ? "Yes" : "No" },
                        { label: "Transmission", value: car?.transmission?.charAt(0).toUpperCase() + car?.transmission?.slice(1) }
                      ].map((spec, index) => (
                        <motion.div 
                          key={index}
                          className="p-3 sm:p-4 bg-gradient-to-br from-red-50 to-gray-50 rounded-lg sm:rounded-xl hover:from-red-100 hover:to-gray-100 transition-all duration-300 border border-red-100 hover:border-red-200"
                          whileHover={{ scale: 1.02 }}
                        >
                          <p className="text-xs sm:text-sm text-gray-600 font-medium mb-1">{spec.label}</p>
                          <p className="font-semibold text-gray-900 text-sm sm:text-base">{spec.value || "-"}</p>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>

                  {/* Features Section */}
                  <motion.div 
                    className="p-4 sm:p-6 lg:p-8 border-t border-gray-200"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.7 }}
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 flex items-center">
                      <HiSparkles className="mr-2 sm:mr-3 text-red-600" />
                      Features & Amenities
                    </h2>
                    
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {car?.features?.map((feature, index) => (
                        <FeatureBadge 
                          key={index} 
                          feature={feature} 
                          isHighlight={index < 3}
                        />
                      ))}
                    </div>
                  </motion.div>

                  {/* Description Section */}
                  <motion.div 
                    className="p-4 sm:p-6 lg:p-8 border-t border-gray-200 bg-gradient-to-br from-red-50 to-white"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                  >
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">About This Vehicle</h2>
                    <p className="text-gray-700 leading-relaxed text-sm sm:text-base lg:text-lg mb-6 sm:mb-8">
                      {car?.description || defaultDescription}
                    </p>

                    {/* Service Highlights */}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div className="space-y-4">
                        <h3 className="font-bold text-gray-800 text-lg flex items-center">
                          <HiLightningBolt className="mr-2 text-yellow-500" />
                          24/7 Road Assistance
                        </h3>
                        <p className="text-gray-600">
                          Our dedicated roadside assistance team is available around the clock. 
                          Simply call +30 6941419012 for immediate support.
                        </p>
                      </div>
                      
                      <div className="space-y-4">
                        <h3 className="font-bold text-gray-800 text-lg flex items-center">
                          <FaShieldAlt className="mr-2 text-green-500" />
                          COVID-19 Safety
                        </h3>
                        <p className="text-gray-600">
                          All vehicles are thoroughly cleaned and sanitized according to 
                          COVID-19 health and safety specifications.
                        </p>
                      </div>
                    </div>

                    {/* Included Services */}
                    <div className="mt-8 p-6 bg-white rounded-2xl border border-green-200">
                      <h3 className="font-bold text-gray-800 mb-4 flex items-center">
                        <FaCheck className="mr-2 text-green-500" />
                        Included in Your Rental
                      </h3>
                      <div className="grid md:grid-cols-2 gap-4">
                        {[
                          "Free cancellation up to 24h",
                          "No credit card fees",
                          "Comprehensive theft protection",
                          "Unlimited mileage within Mykonos",
                          "24/7 customer support",
                          "Mobile assistance hotline"
                        ].map((feature, index) => (
                          <motion.div 
                            key={index}
                            className="flex items-center text-gray-700"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.9 + index * 0.1 }}
                          >
                            <FaCheck className="w-4 h-4 mr-3 text-green-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </motion.div>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                </div>
              </motion.div>

              {/* Right Column - Enhanced Booking Form */}
              <motion.div 
                className="xl:w-1/3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <div className="sticky top-8">
                  {/* Enhanced Price Display */}
                  <motion.div 
                    className="bg-gradient-to-t from-red-500 via-red-600 to-red-700 p-6 rounded-t-3xl text-white shadow-2xl"
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <motion.div 
                          className="flex items-baseline"
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: 0.5, type: "spring" }}
                        >
                          <span className="text-4xl font-bold">
                            €{(formData.pickupDate && formData.dropoffDate) 
                               ? getAveragePriceForDateRange() 
                               : car?.dynamicPrice || car?.basePrice}
                          </span>
                          <span className="text-lg text-red-200 ml-2">/day</span>
                        </motion.div>
                        <p className="text-red-200 text-sm mt-1">
                          {formData.pickupDate && formData.dropoffDate
                            ? `Average for ${formatDateRange(formData.pickupDate)} - ${formatDateRange(formData.dropoffDate)}`
                            : formData.pickupDate 
                              ? `Price for ${formatDateRange(formData.pickupDate)}`
                              : "Starting price"}
                        </p>
                      </div>
                      <motion.div 
                        className="text-right"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.6 }}
                      >
                        <div className="bg-white/20 backdrop-blur-sm rounded-full px-3 py-1">
                          <span className="text-sm font-medium">Best Price</span>
                        </div>
                      </motion.div>
                    </div>

                    {/* Price Breakdown */}
                    {formData.pickupDate && formData.dropoffDate && (
                      <motion.div 
                        className="space-y-3 border-t border-red-900/50 pt-4"
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ delay: 0.7 }}
                      >
                        {calculatePriceBreakdown()?.map((period, index) => (
                          <motion.div
                            key={index}
                            className="flex justify-between text-sm"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.8 + index * 0.1 }}
                          >
                            <span className="text-red-100">
                              {period.days > 1
                                ? `${formatDateRange(period.startDate)} - ${formatDateRange(period.endDate)}`
                                : formatDateRange(period.startDate)}{" "}
                              ({period.days} day{period.days > 1 ? "s" : ""})
                            </span>
                            <span className={period.price !== car?.basePrice ? "text-yellow-300 font-semibold" : "text-white"}>
                              €{period.price}/day
                            </span>
                          </motion.div>
                        ))}
                        
                        <div className="border-t border-red-900/50 pt-3 space-y-2">
                          <div className="flex justify-between text-sm">
                            <span className="text-red-200">Subtotal (excl. VAT):</span>
                            <span>€{(totalPrice / 1.24).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between text-sm">
                            <span className="text-red-200">VAT (24%):</span>
                            <span>€{(totalPrice - totalPrice / 1.24).toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between font-bold text-lg border-t border-red-900/50 pt-2">
                            <span>Total:</span>
                            <span>€{totalPrice.toFixed(2)}</span>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </motion.div>

                  {/* Closure Notice */}
                  <motion.div 
                    className="bg-gradient-to-r from-orange-100 to-yellow-100 border-2 border-orange-300 p-4 sm:p-6 text-center"
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3, type: "spring" }}
                  >
                    <div className="text-2xl sm:text-3xl mb-3">🏖️</div>
                    <h3 className="text-lg sm:text-xl font-bold text-orange-800 mb-2">
                      Sorry, We're Closed!
                    </h3>
                    <p className="text-sm sm:text-base text-orange-700 mb-2">
                      We're currently closed for the 2024-2025 season.
                    </p>
                    <p className="text-xs sm:text-sm text-orange-600">
                      Thank you for your interest! See you next summer! ☀️
                    </p>
                  </motion.div>

                  {/* Enhanced Booking Form */}
                  <motion.div 
                    className="bg-white p-3 sm:p-4 lg:p-6 rounded-b-2xl sm:rounded-b-3xl shadow-2xl opacity-60 pointer-events-none"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 0.6, y: 0 }}
                    transition={{ delay: 0.4 }}
                  >
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                      {/* Date Selection */}
                      <div className="space-y-3 sm:space-y-4">
                        <div>
                          <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center flex-wrap gap-1">
                            <FaCalendarAlt className="mr-2 text-red-600" />
                            <span>Pick-up Date & Time *</span>
                            <span className="ml-1 sm:ml-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                              Athens Time
                            </span>
                          </label>
                          {formData.pickupDate && (
                            <div className="text-xs bg-red-50 text-red-700 p-2 rounded mb-2 overflow-hidden">
                              <strong>Selected:</strong> {moment.tz({
                                year: formData.pickupDate.getFullYear(),
                                month: formData.pickupDate.getMonth(),
                                date: formData.pickupDate.getDate(),
                                hour: formData.pickupDate.getHours(),
                                minute: formData.pickupDate.getMinutes()
                              }, 'Europe/Athens').format('DD/MM/YYYY HH:mm [Athens time]')}
                            </div>
                          )}

                          <DatePicker
                            selected={formData.pickupDate}
                            onChange={(date) => handleDateChange(date, "pickupDate")}
                            onMonthChange={handleMonthChange}
                            customInput={
                              <CustomDateInput
                                placeholder="Select pick-up date & time"
                                isError={!!errors.pickupDate}
                              />
                            }
                            dateFormat="dd/MM/yyyy HH:mm"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={30}
                            minDate={moment().tz('Europe/Athens').toDate()}
                            open={datePickerOpen.pickup}
                            onClickOutside={() => {
                              if (dateTimeComplete.pickup) {
                                setDatePickerOpen(prev => ({ ...prev, pickup: false }));
                              }
                            }}
                            onInputClick={() => setDatePickerOpen({ pickup: true, dropoff: false })}
                            renderDayContents={renderDayContents}
                            wrapperClassName="w-full"
                            popperProps={{
                              strategy: "fixed",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: { rootBoundary: "viewport", padding: 8 }
                                }
                              ]
                            }}
                            filterTime={(time) => {
                              // Filter times based on Athens timezone, not user's local timezone
                              if (formData.pickupDate) {
                                const nowInAthens = moment().tz('Europe/Athens');
                                const todayInAthens = moment().tz('Europe/Athens').startOf('day');
                                
                                // Convert the selected date to Athens timezone for comparison
                                const selectedDateInAthens = moment().tz('Europe/Athens')
                                  .year(formData.pickupDate.getFullYear())
                                  .month(formData.pickupDate.getMonth())
                                  .date(formData.pickupDate.getDate());
                                
                                const selectedDayInAthens = selectedDateInAthens.clone().startOf('day');
                                
                                // Only filter times if the selected date is today in Athens
                                if (selectedDayInAthens.isSame(todayInAthens)) {
                                  const minimumTimeInAthens = nowInAthens.clone().add(1, 'hour');
                                  
                                  // Convert the time option to Athens timezone
                                  // The 'time' parameter comes from DatePicker in user's timezone
                                  // We treat it as Athens time for validation
                                  const timeToCheckInAthens = selectedDateInAthens.clone()
                                    .hour(time.getHours())
                                    .minute(time.getMinutes())
                                    .second(0)
                                    .millisecond(0);
                                  
                                  return timeToCheckInAthens.isSameOrAfter(minimumTimeInAthens);
                                }
                              }
                              return true;
                            }}
                          />
                          {errors.pickupDate && (
                            <motion.p 
                              className="text-red-500 text-xs mt-1"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                            >
                              {errors.pickupDate}
                            </motion.p>
                          )}
                        </div>

                        <div>
                          <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center flex-wrap gap-1">
                            <FaClock className="mr-2 text-red-600" />
                            <span>Drop-off Date & Time *</span>
                            <span className="ml-1 sm:ml-2 text-xs text-red-600 bg-red-50 px-2 py-1 rounded">
                              Athens Time
                            </span>
                          </label>
                          {formData.dropoffDate && (
                            <div className="text-xs bg-red-50 text-red-700 p-2 rounded mb-2 overflow-hidden">
                              <strong>Selected:</strong> {moment.tz({
                                year: formData.dropoffDate.getFullYear(),
                                month: formData.dropoffDate.getMonth(),
                                date: formData.dropoffDate.getDate(),
                                hour: formData.dropoffDate.getHours(),
                                minute: formData.dropoffDate.getMinutes()
                              }, 'Europe/Athens').format('DD/MM/YYYY HH:mm [Athens time]')}
                            </div>
                          )}
                          <DatePicker
                            selected={formData.dropoffDate}
                            onChange={(date) => handleDateChange(date, "dropoffDate")}
                            onMonthChange={handleMonthChange}
                            customInput={
                              <CustomDateInput
                                placeholder="Select drop-off date & time"
                                isError={!!errors.dropoffDate}
                              />
                            }
                            dateFormat="dd/MM/yyyy HH:mm"
                            showTimeSelect
                            timeFormat="HH:mm"
                            timeIntervals={30}
                                                        wrapperClassName="w-full"

                            minDate={formData.pickupDate || moment().tz('Europe/Athens').toDate()}
                            open={datePickerOpen.dropoff}
                            onClickOutside={() => {
                              if (dateTimeComplete.dropoff) {
                                setDatePickerOpen(prev => ({ ...prev, dropoff: false }));
                              }
                            }}
                            onInputClick={() => setDatePickerOpen({ pickup: false, dropoff: true })}
                            renderDayContents={renderDayContents}
                            popperProps={{
                              strategy: "fixed",
                              modifiers: [
                                {
                                  name: "preventOverflow",
                                  options: { rootBoundary: "viewport", padding: 8 }
                                }
                              ]
                            }}
                          />
                          {errors.dropoffDate && (
                            <motion.p 
                              className="text-red-500 text-xs mt-1"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                            >
                              {errors.dropoffDate}
                            </motion.p>
                          )}
                        </div>
                      </div>

                      {/* Location Selection */}
                      <div className="space-y-4">
                        <div className="flex justify-start">
                          <CustomCheckbox
                            checked={sameLocation}
                            onChange={(e) => {
                              setSameLocation(e.target.checked);
                              if (e.target.checked) {
                                setFormData(prev => ({
                                  ...prev,
                                  dropoffLocation: prev.pickupLocation,
                                  customDropoffLocation: prev.customPickupLocation,
                                }));
                              }
                            }}
                            label="Same return location"
                            description="Return the vehicle to the same location"
                          />
                        </div>

                        <div>
                          <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                            <FaMapMarkerAlt className="mr-2 text-red-600" />
                            Pick-up Location *
                          </label>
                          <CustomSelect
                            value={formData.pickupLocation}
                            onChange={(e) => {
                              const newValue = e.target.value;
                              setFormData(prev => ({
                                ...prev,
                                pickupLocation: newValue,
                                customPickupLocation: newValue !== "other" ? "" : prev.customPickupLocation,
                                ...(sameLocation ? {
                                  dropoffLocation: newValue,
                                  customDropoffLocation: newValue !== "other" ? "" : prev.customPickupLocation,
                                } : {}),
                              }));
                            }}
                            onOtherLocationChange={(value) => {
                              setFormData(prev => ({
                                ...prev,
                                customPickupLocation: value,
                                ...(sameLocation ? { customDropoffLocation: value } : {}),
                              }));
                            }}
                            options={locationOptions}
                            placeholder="Select pick-up location"
                            showOtherInput={true}
                            initialCustomValue={formData.customPickupLocation}
                            icon={FaMapMarkerAlt}
                          />
                          {errors.pickupLocation && (
                            <motion.p 
                              className="text-red-500 text-xs mt-1"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                            >
                              {errors.pickupLocation}
                            </motion.p>
                          )}
                        </div>

                        {!sameLocation && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: "auto" }}
                            exit={{ opacity: 0, height: 0 }}
                          >
                            <label className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                              <FaMapPin className="mr-2 text-red-600" />
                              Drop-off Location *
                            </label>
                            <CustomSelect
                              value={formData.dropoffLocation}
                              onChange={(e) => {
                                setFormData(prev => ({
                                  ...prev,
                                  dropoffLocation: e.target.value,
                                  customDropoffLocation: e.target.value !== "other" ? "" : prev.customDropoffLocation,
                                }));
                              }}
                              onOtherLocationChange={(value) => {
                                setFormData(prev => ({
                                  ...prev,
                                  customDropoffLocation: value,
                                }));
                              }}
                              options={locationOptions}
                              placeholder="Select drop-off location"
                              showOtherInput={true}
                              initialCustomValue={formData.customDropoffLocation}
                              icon={FaMapPin}
                            />
                            {errors.dropoffLocation && (
                              <motion.p 
                                className="text-red-500 text-xs mt-1"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                              >
                                {errors.dropoffLocation}
                              </motion.p>
                            )}
                          </motion.div>
                        )}
                      </div>

                      {/* Customer Information */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b border-gray-200 pb-2">
                          <FaUser className="mr-2 text-red-600" />
                          Customer Information
                        </h3>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                            <CustomInput
                              type="text"
                              placeholder="First Name"
                              value={formData.firstName}
                              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                              error={errors.firstName}
                              icon={FaUser}
                            />
                            {errors.firstName && (
                              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                            )}
                          </div>
                          
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                            <CustomInput
                              type="text"
                              placeholder="Last Name"
                              value={formData.lastName}
                              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                              error={errors.lastName}
                              icon={FaUser}
                            />
                            {errors.lastName && (
                              <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                            )}
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                          <CustomInput
                            type="email"
                            placeholder="your@email.com"
                            value={formData.email}
                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                            error={errors.email}
                          />
                          {errors.email && (
                            <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number *</label>
                          <PhoneInput
                            international
                            defaultCountry="GR"
                            value={formData.phone}
                            onChange={(value) => setFormData({ ...formData, phone: value })}
                            placeholder="Enter phone number"
                            className={`w-full h-12 ${
                              errors.phone 
                                ? "border-red-400 focus:border-red-500" 
                                : "border-gray-200 hover:border-red-400 focus:border-red-500"
                            }`}
                            style={{
                              width: '100%',
                              '--PhoneInputCountryFlag-height': '1em',
                              '--PhoneInputCountrySelectArrow-color': '#9CA3AF',
                            }}
                            numberInputProps={{
                              className: `w-full h-12 px-4 border-2 rounded-xl bg-white transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-100 ${
                                errors.phone 
                                  ? "border-red-400 bg-red-50 text-red-600 focus:border-red-500" 
                                  : "border-gray-200 hover:border-red-400 hover:shadow-md focus:border-red-500"
                              }`,
                              style: {
                                paddingLeft: '3.5rem',
                              }
                            }}
                            countrySelectProps={{
                              className: `absolute left-0 top-0 h-12 px-3 border-none bg-transparent focus:outline-none ${
                                errors.phone ? 'text-red-600' : 'text-gray-700'
                              }`
                            }}
                          />
                          {errors.phone && (
                            <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                          )}
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Age *</label>
                          <div className="relative">
                            <select
                              value={formData.age}
                              onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                              className="w-full h-12 px-4 pl-12 border-2 border-gray-200 rounded-xl bg-white appearance-none hover:border-red-400 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500"
                            >
                              {Array.from({ length: 9 }, (_, i) => 18 + i).map(age => (
                                <option key={age} value={age}>{age} years old</option>
                              ))}
                              <option value="26+">26+ years old</option>
                            </select>
                            <FaIdCard className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
                          </div>
                        </div>
                      </div>

                      {/* Additional Options */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b border-gray-200 pb-2">
                          <FaCog className="mr-2 text-red-600" />
                          Additional Options
                        </h3>
                        
                        {loadingAdditionalOptions ? (
                          <div className="flex justify-center py-4">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {additionalOptionsData.length > 0 ? (
                              additionalOptionsData.map((option) => (
                                <CustomCheckbox
                                  key={option._id}
                                  checked={formData.additionalOptions[option._id] || false}
                                  onChange={(e) =>
                                    setFormData({
                                      ...formData,
                                      additionalOptions: {
                                        ...formData.additionalOptions,
                                        [option._id]: e.target.checked,
                                      },
                                    })
                                  }
                                  label={`${option.name}${option.price > 0 ? ` (+€${option.price.toFixed(2)}/day)` : ' (Free)'}`}
                                  description={option.description}
                                />
                              ))
                            ) : (
                              <p className="text-gray-500 text-sm italic">No additional options available</p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Invoice Request Section */}
                      <motion.div 
                        className="space-y-4 p-4 bg-gray-50 rounded-xl border border-gray-200"
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5 }}
                      >
                        <CustomCheckbox
                          checked={wantsInvoice}
                          onChange={(e) => setWantsInvoice(e.target.checked)}
                          label="I need an invoice for this booking"
                          description="Request a formal invoice for business or tax purposes"
                        />

                        <AnimatePresence>
                          {wantsInvoice && (
                            <motion.div 
                              className="space-y-4 mt-4 p-4 bg-white rounded-xl border border-red-200"
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: "auto" }}
                              exit={{ opacity: 0, height: 0 }}
                              transition={{ duration: 0.3 }}
                            >
                              <h4 className="font-semibold text-gray-800 flex items-center">
                                <FaReceipt className="mr-2 text-red-600" />
                                Invoice Details
                              </h4>
                              
                              <div className="grid grid-cols-1 gap-4">
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
                                  <CustomInput
                                    type="text"
                                    placeholder="Company Name"
                                    value={invoiceData.companyName}
                                    onChange={(e) => setInvoiceData({...invoiceData, companyName: e.target.value})}
                                    error={errors.companyName}
                                    icon={FaBuilding}
                                  />
                                  {errors.companyName && (
                                    <p className="text-red-500 text-xs mt-1">{errors.companyName}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">VAT Number *</label>
                                  <CustomInput
                                    type="text"
                                    placeholder="VAT Number"
                                    value={invoiceData.vatNumber}
                                    onChange={(e) => setInvoiceData({...invoiceData, vatNumber: e.target.value})}
                                    error={errors.vatNumber}
                                    icon={FaIdCard}
                                  />
                                  {errors.vatNumber && (
                                    <p className="text-red-500 text-xs mt-1">{errors.vatNumber}</p>
                                  )}
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Address *</label>
                                  <CustomInput
                                    type="text"
                                    placeholder="Street Address"
                                    value={invoiceData.address}
                                    onChange={(e) => setInvoiceData({...invoiceData, address: e.target.value})}
                                    error={errors.address}
                                    icon={FaMapMarkerAlt}
                                  />
                                  {errors.address && (
                                    <p className="text-red-500 text-xs mt-1">{errors.address}</p>
                                  )}
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">City *</label>
                                    <CustomInput
                                      type="text"
                                      placeholder="City"
                                      value={invoiceData.city}
                                      onChange={(e) => setInvoiceData({...invoiceData, city: e.target.value})}
                                      error={errors.city}
                                    />
                                    {errors.city && (
                                      <p className="text-red-500 text-xs mt-1">{errors.city}</p>
                                    )}
                                  </div>

                                  <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code *</label>
                                    <CustomInput
                                      type="text"
                                      placeholder="Postal Code"
                                      value={invoiceData.postalCode}
                                      onChange={(e) => setInvoiceData({...invoiceData, postalCode: e.target.value})}
                                      error={errors.postalCode}
                                    />
                                    {errors.postalCode && (
                                      <p className="text-red-500 text-xs mt-1">{errors.postalCode}</p>
                                    )}
                                  </div>
                                </div>

                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
                                  <CustomInput
                                    type="text"
                                    placeholder="Country"
                                    value={invoiceData.country}
                                    onChange={(e) => setInvoiceData({...invoiceData, country: e.target.value})}
                                  />
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>

                      {/* Additional Notes */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                        <textarea
                          value={formData.notes}
                          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                          rows="3"
                          placeholder="Any special requests or notes..."
                          className="w-full p-4 border-2 border-gray-200 rounded-xl bg-white hover:border-red-400 transition-all duration-300 focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500 resize-none"
                        />
                      </div>

                      {/* Coupon Code */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code</label>
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Enter coupon code"
                            value={formData.couponCode}
                            onChange={(e) => {
                              const code = e.target.value.toUpperCase();
                              setFormData({ ...formData, couponCode: code });
                              validateCoupon(code);
                            }}
                            className={`w-full h-12 px-4 border-2 rounded-xl transition-all duration-300 focus:outline-none focus:ring-4 ${
                              couponStatus.isValid
                                ? "border-green-400 focus:ring-green-100 bg-green-50"
                                : couponStatus.message && !couponStatus.isValid
                                ? "border-red-400 focus:ring-red-100 bg-red-50"
                                : "border-gray-200 focus:ring-red-100 hover:border-red-400 focus:border-red-500"
                            }`}
                          />
                          {couponStatus.message && (
                            <motion.div
                              className={`text-sm mt-2 flex items-center ${
                                couponStatus.isValid ? "text-green-600" : "text-red-600"
                              }`}
                              initial={{ opacity: 0, y: -10 }}
                              animate={{ opacity: 1, y: 0 }}
                            >
                              {couponStatus.isValid ? <FaCheck className="mr-1" /> : "✕"}
                              {couponStatus.message}
                            </motion.div>
                          )}
                        </div>
                      </div>

                      {/* Price Summary */}
                      {formData.pickupDate && formData.dropoffDate && (
                        <motion.div 
                          className="bg-gray-50 p-4 rounded-xl space-y-3"
                          initial={{ opacity: 0, y: 10 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 }}
                        >
                          <h4 className="font-semibold text-gray-800">Booking Summary</h4>
                          
                          {calculatePriceBreakdown()?.map((period, index) => (
                            <div key={index} className="flex justify-between text-sm text-gray-600">
                              <span>
                                {period.days > 1
                                  ? `${formatDateRange(period.startDate)} - ${formatDateRange(period.endDate)}`
                                  : formatDateRange(period.startDate)}{" "}
                                ({period.days} day{period.days > 1 ? "s" : ""})
                              </span>
                              <span>€{period.price}/day</span>
                            </div>
                          ))}

                          <div className="flex justify-between text-sm text-gray-600 pt-2 border-t">
                            <span>Duration:</span>
                            <span>{calculateDurationIn24HourDays(formData.pickupDate, formData.dropoffDate)} day(s)</span>
                          </div>

                          {Object.entries(formData.additionalOptions).map(([key, value]) => {
                            const option = additionalOptionsData.find(opt => opt._id === key);
                            if (option && value) {
                              return (
                                <div key={key} className="flex justify-between text-sm text-gray-600">
                                  <span>{option.name}</span>
                                  <span>{option.price > 0 ? `€${option.price.toFixed(2)}/day` : "Free"}</span>
                                </div>
                              );
                            }
                            return null;
                          })}

                          <div className="flex justify-between text-sm text-gray-600 pt-2 border-t">
                            <span>Subtotal:</span>
                            <span>€{(totalPrice / 1.24).toFixed(2)}</span>
                          </div>

                          {couponStatus.isValid && (
                            <div className="flex justify-between text-sm text-green-600">
                              <span>Coupon Discount:</span>
                              <span>-€{couponStatus.discount.toFixed(2)}</span>
                            </div>
                          )}

                          <div className="flex justify-between text-sm text-gray-600">
                            <span>VAT (24%):</span>
                            <span>€{((totalPrice / 1.24 - (couponStatus.discount || 0)) * 0.24).toFixed(2)}</span>
                          </div>

                          <div className="flex justify-between font-bold text-lg pt-2 border-t border-gray-300">
                            <span>Total:</span>
                            <span className="text-red-600">€{totalPrice.toFixed(2)}</span>
                          </div>
                        </motion.div>
                      )}

                      {/* Payment Method Selection */}
                      <div className="space-y-4">
                        <h3 className="text-lg font-semibold text-gray-800 flex items-center border-b border-gray-200 pb-2">
                          <FaCreditCard className="mr-2 text-red-600" />
                          Payment Method
                        </h3>
                        
                        {loadingPaymentMethods ? (
                          <div className="flex justify-center py-4">
                            <LoadingSpinner size="sm" />
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {/* STRIPE PAYMENT OPTION COMMENTED OUT FOR NOW */}
                            {/*
                            <motion.label
                              className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                                formData.paymentMethod === 'stripe'
                                  ? "border-blue-500 bg-blue-50"
                                  : "border-gray-200 hover:border-blue-300 hover:bg-gray-50"
                              }`}
                              whileHover={{ scale: 1.01 }}
                              whileTap={{ scale: 0.99 }}
                            >
                              <input
                                type="radio"
                                name="paymentMethod"
                                value="stripe"
                                checked={formData.paymentMethod === 'stripe'}
                                onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                                className="hidden"
                              />
                              <div className={`w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0 transition-all duration-300 ${
                                formData.paymentMethod === 'stripe'
                                  ? "border-blue-500 bg-blue-500"
                                  : "border-gray-300"
                              }`}>
                                {formData.paymentMethod === 'stripe' && (
                                  <div className="w-full h-full rounded-full bg-white scale-50 transition-transform duration-300" />
                                )}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-gray-800">
                                    Credit/Debit Card
                                  </span>
                                  <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded-full font-medium">
                                    SECURE
                                  </span>
                                </div>
                                <p className="text-sm text-gray-500 mt-1">
                                  Pay securely with Visa, Mastercard, or other cards
                                </p>
                              </div>
                              <div className="flex items-center space-x-2 ml-3">
                                <img src="https://stripe-images.s3.amazonaws.com/public/logos/cards/visa.png" alt="Visa" className="h-6" />
                                <img src="https://stripe-images.s3.amazonaws.com/public/logos/cards/mastercard.png" alt="Mastercard" className="h-6" />
                                <FaCreditCard className="text-xl text-gray-400" />
                              </div>
                            </motion.label>
                            */}

                            {paymentMethods.length > 0 ? (
                              paymentMethods.filter(method => method.status).map((method) => {
                                // Get payment method icon based on English title
                                const getPaymentIcon = (title) => {
                                  switch (title) {
                                    case 'Cash': return '💰';
                                    case 'Credit Card': return '💳';
                                    case 'Bank Deposit / Transfer': return '🏦';
                                    case 'PayPal': return '🅿️';
                                    case 'Check': return '📋';
                                    case 'Other': return '💼';
                                    default: return '💼';
                                  }
                                };

                                return (
                                  <motion.label
                                    key={method.id}
                                    className={`flex items-center p-4 border-2 rounded-xl cursor-pointer transition-all duration-300 ${
                                      formData.paymentMethod === method.id
                                        ? "border-red-500 bg-red-50"
                                        : "border-gray-200 hover:border-red-300 hover:bg-gray-50"
                                    }`}
                                    whileHover={{ scale: 1.01 }}
                                    whileTap={{ scale: 0.99 }}
                                  >
                                    <input
                                      type="radio"
                                      name="paymentMethod"
                                      value={method.id}
                                      checked={formData.paymentMethod === method.id}
                                      onChange={(e) => setFormData({ ...formData, paymentMethod: e.target.value })}
                                      className="hidden"
                                    />
                                    <div className={`w-5 h-5 rounded-full border-2 mr-3 flex-shrink-0 transition-all duration-300 ${
                                      formData.paymentMethod === method.id
                                        ? "border-red-500 bg-red-500"
                                        : "border-gray-300"
                                    }`}>
                                      {formData.paymentMethod === method.id && (
                                        <div className="w-full h-full rounded-full bg-white scale-50 transition-transform duration-300" />
                                      )}
                                    </div>
                                    <div className="flex-1">
                                      <div className="flex items-center gap-2">
                                        <span className="font-medium text-gray-800">
                                          {method.title_en}
                                        </span>
                                        {method.title_gr && method.title_gr !== method.title_en && (
                                          <span className="text-sm text-gray-500">
                                            ({method.title_gr})
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                    <span className="text-2xl ml-3">
                                      {getPaymentIcon(method.title_en)}
                                    </span>
                                  </motion.label>
                                );
                              })
                            ) : (
                              <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
                                <p className="text-yellow-700 text-sm">
                                  Payment methods are being loaded. Please wait...
                                </p>
                              </div>
                            )}
                            
                            {errors.paymentMethod && (
                              <motion.p 
                                className="text-red-500 text-sm mt-2"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                data-error="true"
                              >
                                {errors.paymentMethod}
                              </motion.p>
                            )}
                          </div>
                        )}
                        
                        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                          <p className="text-red-700 text-sm">
                            <strong>Note:</strong> Payment will be processed upon vehicle pickup. 
                            We accept cash, major credit cards, and bank transfers.
                          </p>
                        </div>
                      </div>

                      {/* Submit Button - Disabled for Closure */}
                      <motion.button
                        type="submit"
                        disabled={true}
                        className="w-full py-4 rounded-xl font-bold text-lg bg-orange-200 text-orange-800 cursor-not-allowed border-2 border-orange-300"
                      >
                        🏖️ Closed for 2024-2025 Season - See You Next Summer! ☀️
                      </motion.button>

                      <p className="text-xs text-gray-500 text-center">
                        By booking, you agree to our terms and conditions. 
                        Free cancellation up to 24 hours before pickup.
                      </p>
                    </form>
                  </motion.div>
                </div>
              </motion.div>
            </motion.div>
          </main>

          <Footer />
        </div>

        {/* Modals */}
          <ImportantInfoModal
            isOpen={showImportantInfo}
            onClose={() => setShowImportantInfo(false)}
          />

          <InsuranceModal
            isOpen={showInsurance}
            onClose={() => setShowInsurance(false)}
          />

          <SuccessModal
            isOpen={showSuccess}
            onClose={() => {
              setShowSuccess(false);
              navigate('/');
              window.scrollTo(0, 0);
            }}
          />

          {/* PAYMENT MODAL COMMENTED OUT FOR NOW */}
          {/*
          <PaymentModal
            isOpen={showPayment}
            onClose={handlePaymentModalClose}
            rentalData={pendingRentalData}
            onPaymentSuccess={handlePaymentSuccess}
            onPaymentError={handlePaymentError}
          />
          */}

          <ToastContainer position="top-right" autoClose={5000} />

          {/* Enhanced Styles */}
        <style jsx>{`
          .react-datepicker__day {
            color: #1f2937 !important;
            font-weight: 500;
            transition: all 0.2s ease;
          }
          
          .react-datepicker__day--selected {
            background-color: #2563eb !important;
            color: white !important;
            border-radius: 8px;
          }
          
          .react-datepicker__day:not(.react-datepicker__day--disabled):hover {
            background-color: #dbeafe !important;
            border-radius: 8px;
            transform: scale(1.05);
          }
          .react-datepicker__day--today {
            font-weight: bold !important;
            color: #1e3a8a !important;
          }
          
          /* Disabled/unavailable dates */
          .react-datepicker__day--disabled {
            text-decoration: line-through;
            color: #9ca3af !important;
            cursor: not-allowed;
            opacity: 0.5;
          }
          
          /* Time list items */
          .react-datepicker__time-list-item:not(.react-datepicker__time-list-item--disabled) {
            color: #1f2937 !important;
          }
          
          .react-datepicker__time-list-item--disabled {
            color: #9ca3af !important;
            text-decoration: line-through;
          }
          
          /* Selected time */
          .react-datepicker__time-list-item--selected {
            background-color: #1e3a8a !important;
            color: white !important;
          }
        `}
      </style>
    </>
  );
};

export default CarDetails;